import axios from "axios";


// const API = axios.create({ baseURL: 'http://localhost:5000'})
const API = axios.create({ baseURL: process.env.REACT_APP_API });

// External company API — resolves subdomain to companyId (no auth token attached)
const COMPANY_API = axios.create({
  baseURL: process.env.REACT_APP_COMPANY_API_URL,
});
export const resolveSubdomain = (subdomain) =>
  COMPANY_API.get(`/Companies/by-subdomain/${subdomain}`);

// Auth state management
let currentAuthToken = null;
const shouldLogAuthDiagnostics = () => process.env.NODE_ENV !== "test";
const logAuthDiagnostic = (...args) => {
  if (shouldLogAuthDiagnostics()) {
    console.log(...args);
  }
};

// Initialize auth token from localStorage
const initializeAuth = () => {
  const localuser = localStorage.getItem("profile");
  if (localuser) {
    try {
      const userData = JSON.parse(localuser);
      currentAuthToken = userData.token;
      logAuthDiagnostic(
        "✅ Auth token loaded from localStorage:",
        currentAuthToken ? "TOKEN EXISTS" : "NO TOKEN",
      );
    } catch (error) {
      console.error("Error parsing auth data:", error);
      currentAuthToken = null;
    }
  } else {
    logAuthDiagnostic("❌ No profile data in localStorage");
    currentAuthToken = null;
  }
};

// Update auth token when auth state changes
export const updateAuthToken = (token = null) => {
  currentAuthToken = token;
  logAuthDiagnostic("🔄 Auth token updated:", token ? "TOKEN SET" : "TOKEN CLEARED");
};

// Clear auth token
export const clearAuthToken = () => {
  currentAuthToken = null;
  logAuthDiagnostic("🗑️ Auth token cleared");
};

// Initialize auth on module load
initializeAuth();

API.interceptors.request.use((req) => {
  logAuthDiagnostic(`📡 API Request: ${req.method.toUpperCase()} ${req.url}`);
  if (currentAuthToken) {
    req.headers.authorization = `Bearer ${currentAuthToken}`;
    logAuthDiagnostic("✅ Authorization header added");
  } else {
    logAuthDiagnostic(
      "⚠️ WARNING: No auth token! Request will likely be rejected with 403",
    );
  }

  return req;
});

const applyFilters = (filters) => {
  if (!filters || Object.keys(filters).length === 0 || filters === null) {
    return "";
  }
  const filterParams = Object.entries(filters)
    .filter(
      ([_, value]) => value !== undefined && value !== "" && value !== null,
    )
    .map(([key, value]) => `${key}=${value}`);
  return filterParams.join("&");
};

// export const fetchInvoices =() => API.get('/invoices')
export const fetchInvoice = (id) => API.get(`/invoices/${id}`);
export const addInvoice = (invoice) => API.post("/invoices", invoice);
export const updateInvoice = (id, updatedInvoice) =>
  API.patch(`/invoices/${id}`, updatedInvoice);
export const deleteInvoice = (id) => API.delete(`/invoices/${id}`);
export const fetchInvoicesByUser = (searchQuery, page, pageSize) => {
  const clientId = searchQuery.clientId;
  const sortBy = searchQuery.sortBy || "createdAt";
  const sortOrder =
    searchQuery.sortOrder !== undefined ? searchQuery.sortOrder : -1;
  const params = new URLSearchParams({
    searchQuery: searchQuery.search || "",
    page,
    pageSize,
    sortBy,
    sortOrder,
  });
  if (searchQuery.status) params.set("status", searchQuery.status);
  if (searchQuery.type) params.set("type", searchQuery.type);
  if (searchQuery.dateFrom) params.set("dateFrom", searchQuery.dateFrom);
  if (searchQuery.dateTo) params.set("dateTo", searchQuery.dateTo);
  const url = clientId
    ? `/invoices/client/${clientId}?${params.toString()}`
    : `/invoices?${params.toString()}`;
  return API.get(url);
};
export const fetchInvoicesByRental = (rentalId) =>
  API.get(`/invoices/byRental/${rentalId}`);
export const fetchInvoiceNumberByUser = (searchQuery) =>
  API.get(
    `/invoices/lastInvoice?searchQuery=${searchQuery.search || ""}&type=${encodeURIComponent(searchQuery.type || "Invoice")}`,
  );
export const downloadPdfInvoice = (id, lang) =>
  API.get(`/invoices/pdf/${id}/${lang}`, {
    responseType: "blob",
  });

export const reGenerateTransactions = (id) =>
  API.patch(`/invoices/${id}/re-generate-transactions`);

export const emailPdfInvoice = (id) =>
  API.patch(`/invoices/pdf/${id}/send`, {});

export const convertToInvoice = (id) => API.post(`/invoices/${id}/convert`);

export const fetchClient = (id) => API.get(`/clients/${id}`);
export const fetchClients = (page) => API.get(`/clients?page=${page}`);
export const addClient = (client) => API.post("/clients", client);
export const updateClient = (id, updatedClient) =>
  API.patch(`/clients/${id}`, updatedClient);
export const deleteClient = (id) => API.delete(`/clients/${id}`);
export const fetchClientsByUser = (searchQuery, page, pageSize) =>
  API.get(
    `/clients/user?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}`,
  );
export const fetchClientsBySearchQuery = (searchQuery, page, pageSize) =>
  API.get(
    `/clients/user?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}`,
  );

export const signIn = (formData) => API.post("/users/signin", formData);
export const getDbInitStatus = () => API.get("/system/init-status");

// Client Portal Authentication
export const clientPortalSignin = (formData) =>
  API.post("/client-portal/signin", formData);
export const setClientPortalPassword = (data) =>
  API.post("/client-portal/admin/set-password", data);
export const toggleClientPortalAccess = (data) =>
  API.post("/client-portal/admin/toggle-access", data);
export const forgot = (formData) =>
  API.post("/users/forgot-password", formData);
export const reset = ({ token, ...formData }) =>
  API.post(`/users/reset-password/${token}`, formData);
export const changeFirstPassword = (data) =>
  API.post("/users/change-first-password", data);
export const changePassword = (data) =>
  API.post("/users/change-password", data);

export const adminResetUserPassword = (userId, newPassword) =>
  API.post(`/users/${userId}/reset-password`, { newPassword });

export const mfaStatus = () => API.get("/users/mfa/status");
export const mfaSetup = () => API.post("/users/mfa/setup");
export const mfaVerifySetup = (code) => API.post("/users/mfa/verify-setup", { code });
export const mfaDisable = (code) => API.post("/users/mfa/disable", { code });
export const mfaVerifyLogin = (code, mfaTempToken) =>
  API.post("/users/mfa/verify-login", { code, mfaTempToken });

export const fetchProfilesBySearch = (searchQuery) =>
  API.get(
    `/profiles/search?searchQuery=${searchQuery.search || searchQuery.year || "none"
    }`,
  );
export const fetchProfile = (id) => API.get(`/profiles/${id}`);
export const fetchProfiles = () => API.get("/profiles");
export const fetchProfilesByUser = (searchQuery) =>
  API.get(`/profiles?searchQuery=${searchQuery.search}`);
export const createProfile = (newProfile) => API.post("/profiles", newProfile);
export const updateProfile = (id, updatedProfile) =>
  API.patch(`/profiles/${id}`, updatedProfile);
export const deleteProfile = (id) => API.delete(`/profiles/${id}`);

export const uploadFile = (formData) => API.post(`/upload`, formData);
export const fetchFileById = (id) => API.get(`/upload/${id}`);
export const fetchFilesByUser = (searchQuery) =>
  API.get(`/upload?searchQuery=${searchQuery.search}`);

export const fetchCategory = (id) => API.get(`/categories/${id}`);
export const addCategory = (Category) => API.post("/categories", Category);
export const updateCategory = (id, updatedCategory) =>
  API.patch(`/categories/${id}`, updatedCategory);
export const deleteCategory = (id) => API.delete(`/categories/${id}`);
export const fetchCategoriesByUser = (searchQuery, page) =>
  API.get(`/categories?searchQuery=${searchQuery.search}&page=${page}`);
export const fetchAllCategories = () => API.get("/categories?all=true");

export const fetchItem = (id) => API.get(`/items/${id}`);
export const addItem = (Item) => API.post("/items", Item);
export const updateItem = (id, updatedItem) =>
  API.patch(`/items/${id}`, updatedItem);
export const deleteItem = (id) => API.delete(`/items/${id}`);
export const fetchItemsByUser = (
  searchQuery,
  page,
  pageSize = 150,
  category = "",
) =>
  API.get(
    `/items?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}&category=${category}&selector=${searchQuery.selector || ""}`,
  );
export const lastModified = (searchQuery, page) =>
  API.get(`/items/lastModified?searchQuery=${searchQuery.search}`);
export const contactUs = (formData) => API.post("/contactus", formData);

// Per-tenant ERP-local profile (singleton) — not the website company doc.
export const fetchCompanyProfile = () => API.get("/company-profile");
export const patchCompanyProfile = (body) =>
  API.patch("/company-profile", body);

// Read-only proxies — both endpoints delegate to the website API on the server.
export const fetchCompany = (id) => API.get(`/companies/${id}`);
export const fetchCompanyPublicDetails = (id) =>
  API.get(`/companies/${id}/public`);

export const fetchUserGroup = (id) => API.get(`/groups/${id}`);
export const fetchUserGroups = (searchQuery, page, pageSize = 15) =>
  API.get(
    `/groups?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}`,
  );
export const addUserGroup = (UserGroup) => API.post("/groups", UserGroup);
export const updateUserGroup = (id, updatedUserGroup) =>
  API.patch(`/groups/${id}`, updatedUserGroup);
export const deleteUserGroup = (id) => API.delete(`/groups/${id}`);
export const fetchRoles = () => API.get(`/groups/roles`);
export const seedDefaultUserGroups = () => API.post(`/groups/seed-defaults`);

export const fetchUser = (id) => API.get(`/users/${id}`);
export const fetchUsers = (searchQuery, page, pageSize = 15) =>
  API.get(
    `/users?search=${encodeURIComponent(searchQuery?.search || "")}&page=${page}&pageSize=${pageSize}`,
  );
export const addUser = (User) => API.post("/users", User);
export const updateUser = (id, updatedUser) =>
  API.patch(`/users/${id}`, updatedUser);
export const deleteUser = (id) => API.delete(`/users/${id}`);

export const fetchRents = (searchQuery, page, pageSize = 15) =>
  API.get(
    `/rentals?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}`,
  );
export const fetchUnpaidRents = (searchQuery, page, pageSize = 15) =>
  API.get(
    `/rentals/unpaid?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}`,
  );

export const fetchRent = (id) => API.get(`/rentals/${id}`);
export const addRent = (Rent) => API.post("/rentals", Rent);
export const updateRent = (id, updatedRent) =>
  API.patch(`/rentals/${id}`, updatedRent);
export const deleteRent = (id) => API.delete(`/rentals/${id}`);

export const fetchUnits = (searchQuery, page, pageSize = 15) =>
  API.get(
    `/units?searchQuery=${searchQuery.search}&isVacant=${searchQuery.isVacant}&page=${page}&pageSize=${pageSize}`,
  );
export const fetchUnit = (id) => API.get(`/units/${id}`);
export const addUnit = (Unit) => API.post("/units", Unit);
export const updateUnit = (id, updatedUnit) =>
  API.patch(`/units/${id}`, updatedUnit);
export const deleteUnit = (id) => API.delete(`/units/${id}`);

export const fetchBuildings = (searchQuery, page, pageSize = 15) =>
  API.get(
    `/buildings?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}`,
  );
export const fetchBuilding = (id) => API.get(`/buildings/${id}`);
export const addBuilding = (Building) => API.post("/buildings", Building);
export const updateBuilding = (id, updatedBuilding) =>
  API.patch(`/buildings/${id}`, updatedBuilding);
export const deleteBuilding = (id) => API.delete(`/buildings/${id}`);

export const fetchTransactions = (searchQuery, page, pageSize = 15) => {
  const queryParams = new URLSearchParams();

  // Add filters to query params
  if (searchQuery) {
    Object.keys(searchQuery).forEach((key) => {
      if (
        searchQuery[key] !== undefined &&
        searchQuery[key] !== null &&
        searchQuery[key] !== ""
      ) {
        if (Array.isArray(searchQuery[key])) {
          searchQuery[key].forEach((value) => queryParams.append(key, value));
        } else {
          queryParams.append(key, searchQuery[key]);
        }
      }
    });
  }

  // Add pagination params
  queryParams.append("page", page);
  queryParams.append("pageSize", pageSize);

  return API.get(`/transactions?${queryParams.toString()}`);
};
export const fetchTransaction = (id) => API.get(`/transactions/${id}`);
export const addTransaction = (Transaction) =>
  API.post("/transactions", Transaction);
export const updateTransaction = (id, updatedTransaction) =>
  API.patch(`/transactions/${id}`, updatedTransaction);
export const deleteTransaction = (id) => API.delete(`/transactions/${id}`);
export const fetchTrialBalance = () => API.get(`/transactions/trial-balance`);
export const fetchBalanceSheet = () => API.get(`/transactions/balance-sheet`);
export const fetchCashFlow = () => API.get(`/transactions/cash-flow`);
export const fetchAgingReport = (params = {}) => {
  const query = new URLSearchParams();
  if (params.asOf) query.append("asOf", params.asOf);
  return API.get(`/invoices/aging-report?${query.toString()}`);
};
export const fetchGeneralLedger = (accountId, params = {}) => {
  const query = new URLSearchParams();
  if (params.fromDate) query.append("fromDate", params.fromDate);
  if (params.toDate) query.append("toDate", params.toDate);
  if (params.status) query.append("status", params.status);
  return API.get(`/transactions/general-ledger/${accountId}?${query.toString()}`);
};

export const fetchAccountStatement = (clientId, params = {}) => {
  const query = new URLSearchParams();
  if (params.fromDate) query.append("fromDate", params.fromDate);
  if (params.toDate) query.append("toDate", params.toDate);
  return API.get(`/invoices/account-statement/${clientId}?${query.toString()}`);
};

export const fetchVatReport = (params = {}) => {
  const query = new URLSearchParams();
  if (params.fromDate) query.append("fromDate", params.fromDate);
  if (params.toDate) query.append("toDate", params.toDate);
  return API.get(`/invoices/vat-report?${query.toString()}`);
};

export const fetchAccounts = (searchQuery, page, pageSize = 15) =>
  API.get(
    `/accounts?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}`,
  );
export const fetchAccount = (id) => API.get(`/accounts/${id}`);
export const addAccount = (Account) => API.post("/accounts", Account);
export const updateAccount = (id, updatedAccount) =>
  API.patch(`/accounts/${id}`, updatedAccount);
export const deleteAccount = (id) => API.delete(`/accounts/${id}`);

// Payment account selection APIs
export const fetchAccountsByParent = (parentIds) =>
  API.get(`/accounts/by-parent/${parentIds}`);
export const fetchLeafAccountsByParent = (parentIds) =>
  API.get(`/accounts/leaf-accounts/${parentIds}`);

export const fetchLetters = (searchQuery, page, pageSize = 15, letterType) => {
  // Build query parameters
  let queryParams = `letterType=${letterType}&page=${page}&pageSize=${pageSize}`;

  // Add search query if provided
  if (searchQuery.search) {
    queryParams += `&searchQuery=${searchQuery.search}`;
  }

  // Add status filter if provided
  if (searchQuery.status) {
    queryParams += `&status=${searchQuery.status}`;
  }

  return API.get(`/letters?${queryParams}`);
};
export const fetchLetter = (id) => API.get(`/letters/${id}`);
export const addLetter = (Letter) => API.post("/letters", Letter);
export const updateLetter = (id, updatedLetter) =>
  API.put(`/letters/${id}`, updatedLetter);
export const deleteLetter = (id) => API.delete(`/letters/${id}`);
export const downloadPdfLetter = (id, lang) =>
  API.get(`/letters/pdf/${id}/${lang}`, {
    responseType: "blob",
  });

// api/index.js

// Fetch purchase invoices with optional search query and pagination
export const fetchPurchaseInvoices = (
  searchQuery = {},
  page = 1,
  pageSize = 15,
) =>
  API.get(
    `/purchases?searchQuery=${searchQuery.search || ""}&status=${searchQuery.status || ""}&type=${searchQuery.type || ""}&dateFrom=${searchQuery.dateFrom || ""}&dateTo=${searchQuery.dateTo || ""}&page=${page}&pageSize=${pageSize}&selector=${searchQuery.selector || ""}`,
  );

// Fetch a single purchase invoice by ID
export const fetchPurchaseInvoice = (id) => API.get(`/purchases/${id}`);

// Add a new purchase invoice
export const addPurchaseInvoice = (purchaseInvoice) =>
  API.post("/purchases", purchaseInvoice);

// Update an existing purchase invoice by ID
export const updatePurchaseInvoice = (id, updatedPurchaseInvoice) =>
  API.put(`/purchases/${id}`, updatedPurchaseInvoice);

// Delete a purchase invoice by ID
export const deletePurchaseInvoice = (id) => API.delete(`/purchases/${id}`);

// Download a PDF of the purchase invoice
export const downloadPdfPurchaseInvoice = (id, lang) =>
  API.get(`/purchases/pdf/${id}/${lang}`, {
    responseType: "blob",
  });

/* Fetch the last invoice number for the current user with optional search query
 *
 * @param {Object} searchQuery - Search query object
 * @param {String} searchQuery.search - Search query string
 * @returns {Promise<Number>} Last invoice number
 */
export const fetchLastPurchaseNumber = (searchQuery) =>
  API.get(`/purchases/lastInvoice?searchQuery=${searchQuery.search}`);
export const emailPurchasePdfInvoice = (id) =>
  API.patch(`/purchases/pdf/${id}/send`, {});

// Regenerate purchase transactions
export const reGeneratePurchaseTransactions = (id) =>
  API.patch(`/purchases/${id}/re-generate-transactions`);

export const convertPurchaseOrderToInvoice = (id) =>
  API.post(`/purchases/${id}/convert`);

export const seedAccount = () => API.post("/seed-account", null);

export const createPayroll = (payrollData) =>
  API.post("/hr/payrolls", payrollData);

export const fetchPayrolls = (params = {}) => {
  const queryParams = new URLSearchParams();

  // Add pagination parameters
  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);

  // Add filter parameters
  if (params.paymentStatus)
    queryParams.append("paymentStatus", params.paymentStatus);
  if (params.month) queryParams.append("month", params.month);
  if (params.year) queryParams.append("year", params.year);
  if (params.userId) queryParams.append("userId", params.userId);
  if (params.isProcessed !== undefined)
    queryParams.append("isProcessed", params.isProcessed);

  const queryString = queryParams.toString();
  return API.get(`/hr/payrolls/${queryString ? `?${queryString}` : ""}`);
};

export const fetchPayroll = (id) => API.get(`/hr/payrolls/${id}`);

export const updatePayroll = (id, payrollData) =>
  API.put(`/hr/payrolls/${id}`, payrollData);

export const fetchPayrollSummary = (filters) =>
  API.get("/hr/payrolls/summary", { params: filters });

export const bulkCreatePayrolls = (payrollData) =>
  API.post("/hr/payrolls/bulk", payrollData);

export const processPayroll = (id) => API.post(`/hr/payrolls/${id}/process`);

export const deletePayroll = (id) => API.delete(`/hr/payrolls/${id}`);

export const updatePaymentStatus = (id, statusData) =>
  API.post(`/hr/payrolls/${id}/payment`, statusData);

// Payroll Payment APIs
export const createPayrollPayment = (paymentData) =>
  API.post("/hr/payroll-payments", paymentData);

export const fetchPayrollPayments = (params = {}) => {
  const queryParams = new URLSearchParams();

  // Add pagination and filter parameters
  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.employeeId) queryParams.append("employeeId", params.employeeId);
  if (params.startDate) queryParams.append("startDate", params.startDate);
  if (params.endDate) queryParams.append("endDate", params.endDate);
  if (params.paymentMethod)
    queryParams.append("paymentMethod", params.paymentMethod);
  if (params.status) queryParams.append("status", params.status);

  const queryString = queryParams.toString();
  return API.get(`/hr/payroll-payments${queryString ? `?${queryString}` : ""}`);
};

export const fetchPayrollPaymentById = (id) =>
  API.get(`/hr/payroll-payments/${id}`);

export const updatePayrollPaymentStatus = (id, statusData) =>
  API.patch(`/hr/payroll-payments/${id}/status`, statusData);

export const deletePayrollPayment = (id, data) =>
  API.delete(`/hr/payroll-payments/${id}`, { data });

export const fetchEmployeePaymentHistory = (employeeId, params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.startDate) queryParams.append("startDate", params.startDate);
  if (params.endDate) queryParams.append("endDate", params.endDate);
  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);

  const queryString = queryParams.toString();
  return API.get(
    `/hr/payroll-payments/employee/${employeeId}${queryString ? `?${queryString}` : ""
    }`,
  );
};

export const recordAttendance = (attendanceData) =>
  API.post("/hr/attendances", attendanceData);

export const fetchAttendanceRecords = () => API.get(`/hr/attendances/`);

// Attendance Reporting APIs
export const getAttendanceReports = (filters = {}) => {
  const queryParams = new URLSearchParams();
  if (filters.startDate) queryParams.append("startDate", filters.startDate);
  if (filters.endDate) queryParams.append("endDate", filters.endDate);
  if (filters.employeeId) queryParams.append("employeeId", filters.employeeId);
  if (filters.department) queryParams.append("department", filters.department);
  if (filters.status) queryParams.append("status", filters.status);
  if (filters.page) queryParams.append("page", filters.page);
  if (filters.limit) queryParams.append("limit", filters.limit);

  return API.get(`/hr/attendances/reports?${queryParams.toString()}`);
};

export const getEmployeeAttendanceSummary = (startDate, endDate) => {
  const queryParams = new URLSearchParams();
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);

  return API.get(`/hr/attendances/summary/employees?${queryParams.toString()}`);
};

export const getDailyAttendanceSummary = (startDate, endDate) => {
  const queryParams = new URLSearchParams();
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);

  return API.get(`/hr/attendances/summary/daily?${queryParams.toString()}`);
};

export const getBarcodeSettings = (searchQuery, page, pageSize = 15) =>
  API.get(
    `/settings/barcode?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}`,
  );
export const getBarcodeSettingsById = (id) =>
  API.get(`/settings/barcode/${id}`);
export const createBarcodeSettings = (barcodeData) =>
  API.post(`/settings/barcode/`, barcodeData);
export const updateBarcodeSettings = (id, updatedData) =>
  API.patch(`/settings/barcode/${id}`, updatedData);
export const deleteBarcodeSettings = (id) =>
  API.delete(`/settings/barcode/${id}`);

// Tax Settings
// export const fetchAllTaxSettings = () => API.get(`/settings/tax`);
export const fetchAllTaxSettings = (searchQuery, page, pageSize = 15) =>
  API.get(
    `/settings/tax?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}`,
  );
export const addTaxSettings = (tax) => API.post("/settings/tax/", tax);
export const fetchTaxSettingsById = (id) => API.get(`/settings/tax/${id}`);
export const updateTax = (id, newTax) => API.put(`/settings/tax/${id}`, newTax);
export const deleteTax = (id) => API.delete(`/settings/tax/${id}`);

// Cost Centers
export const getCostCenters = () => API.get("/cost-centers");
export const fetchAllCostCenters = () => API.get("/cost-centers?all=true");
export const getCostCenter = (id) => API.get(`/cost-centers/${id}`);
export const createCostCenter = (costCenterData) =>
  API.post("/cost-centers", costCenterData);
export const updateCostCenter = (id, updateData) =>
  API.put(`/cost-centers/${id}`, updateData);
export const deleteCostCenter = (id) => API.delete(`/cost-centers/${id}`);

// Assets
export const fetchAssets = (searchQuery = {}, page, pageSize = 15) =>
  API.get(
    `/assets?searchQuery=${searchQuery.search || ""}&status=${searchQuery.status || ""}&dateFrom=${searchQuery.dateFrom || ""}&dateTo=${searchQuery.dateTo || ""}&page=${page}&pageSize=${pageSize}`,
  );
export const fetchAssetById = (id) => API.get(`/assets/${id}`);
export const addAsset = (asset) => API.post("/assets", asset);
export const updateAsset = (id, updateData) =>
  API.patch(`/assets/${id}`, updateData);
export const deleteAsset = (id) => API.delete(`/assets/${id}`);

// profitCenters
export const getProfitCenters = (searchQuery, page, pageSize = 15) =>
  API.get(
    `/profit-centers?searchQuery=${searchQuery?.search || ""}&page=${page}&pageSize=${pageSize}`,
  );
export const fetchAllProfitCenters = () => API.get("/profit-centers?all=true");

export const getProfitCenter = (id) => API.get(`/profit-centers/${id}`);

export const createProfitCenter = (profitCenterData) =>
  API.post("/profit-centers", profitCenterData);

export const updateProfitCenter = (id, updatedData) =>
  API.patch(`/profit-centers/${id}`, updatedData);

export const deleteProfitCenter = (id) => API.delete(`/profit-centers/${id}`);

// projects
export const getProjects = (searchQuery, page, pageSize = 10) =>
  API.get(
    `/projects?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}`,
  );
export const getProject = (id) => API.get(`/projects/${id}`);

export const createProject = (ProjectsData) =>
  API.post(`/projects`, ProjectsData);

export const updateProject = (id, updatedData) =>
  API.patch(`/projects/${id}`, updatedData);

export const deleteProject = (id) => API.delete(`/projects/${id}`);

// SMS Providers
export const getSMSProviders = (searchQuery, page, pageSize = 15) =>
  API.get(
    `/settings/sms-provider?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}`,
  );

export const getSMSProvidersById = (id) => API.get(`/sms-providers/${id}`);

export const createSMSProviders = (smsData) =>
  API.post(`/settings/sms-provider`, smsData);

export const updateSMSProviders = (id, updatedData) =>
  API.patch(`/settings/sms-provider/${id}`, updatedData);

export const deleteSMSProviders = (id) =>
  API.delete(`/settings/sms-provider/${id}`);

// SMS Settings API
export const fetchSmsSettings = () => API.get("/settings/sms-settings");
export const createSmsSettings = (settings) =>
  API.post("/settings/sms-settings", settings);
export const updateSmsSettings = (id, updatedSettings) =>
  API.patch(`/settings/sms-settings/${id}`, updatedSettings);

// Notifications
export const getNotifications = (searchQuery, page, pageSize = 15) =>
  API.get(
    `/notifications?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}`,
  );

export const getNotification = (id) => API.get(`/notifications/${id}`);

export const createNotification = (notificationData) =>
  API.post("/notifications", notificationData);

export const updateNotification = (id, updatedData) =>
  API.patch(`/notifications/${id}`, updatedData);

export const cancelNotification = (id) =>
  API.patch(`/notifications/${id}/cancel`);
export const reSendNotification = (id) =>
  API.patch(`/notifications/${id}/resend`);
export const retryNotification = (id) =>
  API.patch(`/notifications/${id}/retry`);
export const deleteNotification = (id) => API.delete(`/notifications/${id}`);

///Vouchers///
export const createVouchers = (voucherData) =>
  API.post(`/vouchers`, voucherData);
export const getAllVouchers = () => API.get(`/vouchers`);
export const updateVouchers = (code, voucherData) =>
  API.patch(`/vouchers/${code}`, voucherData);
export const useVouchers = (voucherData) =>
  API.post(`/vouchers/use`, voucherData);
export const deleteVouchers = (code) => API.delete(`/vouchers/${code}`);

// Coupons

export const fetchCoupons = (searchQuery, page, pageSize = 15) =>
  API.get(
    `/coupons?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}`,
  );

export const fetchCoupon = (id) => API.get(`/coupons/${id}`);

export const fetchCouponByCode = (code) => API.get(`/coupons/code/${code}`);

export const createCoupon = (couponData) => API.post(`/coupons`, couponData);

export const updateCoupon = (id, updatedData) =>
  API.patch(`/coupons/${id}`, updatedData);

export const deleteCoupon = (id) => API.delete(`/coupons/${id}`);

// Communications
export const connectWhatsApp = () =>
  API.post(`/communication/whatsapp/connect`);
export const disconnectWhatsApp = () =>
  API.post(`/communication/whatsapp/disconnect`);

// WhatsApp device management (new backend-proxied API)
export const waSetupDevice = () => API.post("/whatsapp/setup");
export const waDeviceStatus = () => API.get("/whatsapp/status");
export const waLoginQR = () => API.get("/whatsapp/login");
export const waLoginWithCode = (phone) => API.post(`/whatsapp/login/code?phone=${encodeURIComponent(phone)}`);
export const waLogoutDevice = () => API.post("/whatsapp/logout");
export const waGetWsUrl = () => API.get("/whatsapp/ws-url");
// Email
export const createSmtpConfig = (configData) =>
  API.post(`/communication/smtp/config`, configData);
export const createSmtpTest = (configData) =>
  API.post(`/communication/smtp/test`, configData);

export const fetchSmtpConfig = () => API.get(`/communication/smtp/config`);

// productPackages
export const fetchProductPackages = () => API.get(`/packages`);
export const fetchProductPackageById = (id) => API.get(`/packages/${id}`);
export const createProductPackage = (PackageData) =>
  API.post(`/packages`, PackageData);
export const updateProductPackage = (id, packageData) =>
  API.put(`/packages/${id}`, packageData);
export const deleteProductPackage = (id) => API.delete(`/packages/${id}`);
export const fetchProductPackagesByLang = (lang) =>
  API.get(`packages/lang/${lang}`);

// Features
export const fetchFeatures = () => API.get(`/features`);
export const fetchFeaturesByLang = (lang) => API.get(`/features/lang/${lang}`);
export const createFeature = (featureData) =>
  API.post(`/features`, featureData);
export const updateFeature = (id, updatedData) =>
  API.patch(`/features/${id}`, updatedData);
export const deleteFeature = (id) => API.delete(`/features/${id}`);

// Payments
export const fetchPayments = (searchQuery, page, pageSize = 15) =>
  API.get(
    `/payments?${applyFilters(searchQuery)}&page=${page}&pageSize=${pageSize}`,
  );
export const fetchPayment = (id) => API.get(`/payments/${id}`);
export const createPayment = (paymentData) =>
  API.post("/payments", paymentData);
export const updatePayment = (id, paymentData) =>
  API.patch(`/payments/${id}`, paymentData);
export const deletePayment = (id) => API.delete(`/payments/${id}`);
export const fetchPaymentsByInvoice = (invoiceId) =>
  API.get(`/payments/invoice/${invoiceId}`);
export const fetchPaymentsByPayroll = (payrollId) =>
  API.get(`/payments/payroll/${payrollId}`);
export const fetchPaymentsByRental = (rentalId) =>
  API.get(`/payments/rental/${rentalId}`);
export const printPaymentReceipt = (id, lang) =>
  API.get(`/payments/receipt/${id}/${lang}`, {
    responseType: "blob",
  });
export const updatePaymentAttachment = (id, attachment) =>
  API.patch(`/payments/${id}/attachment`, attachment);
export const updateInvoiceAttachment = (id, attachment) =>
  API.patch(`/invoices/${id}/attachment`, attachment);
export const updateTransactionAttachment = (id, attachment) =>
  API.patch(`/transactions/${id}/attachment`, attachment);
export const updatePurchaseInvoiceAttachment = (id, attachment) =>
  API.patch(`/purchases/${id}/attachment`, attachment);
export const updateAssetAttachment = (id, attachment) =>
  API.patch(`/assets/${id}/attachment`, attachment);

export const updatePropertyAttachment = (id, attachment) =>
  API.patch(`/properties/${id}/attachment`, attachment);
// POS Terminal API functions
export const fetchPosTerminals = (
  searchQuery,
  page,
  pageSize = 15,
  isActive,
) => {
  let url = `/api/pos/terminals?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}`;
  if (isActive !== undefined) {
    url += `&isActive=${isActive}`;
  }
  return API.get(url);
};

export const fetchPosTerminal = (id) => API.get(`/api/pos/terminals/${id}`);

export const createPosTerminal = (posTerminalData) =>
  API.post("/api/pos/terminals", posTerminalData);

export const updatePosTerminal = (id, posTerminalData) =>
  API.put(`/api/pos/terminals/${id}`, posTerminalData);

export const deletePosTerminal = (id) => API.delete(`/api/pos/terminals/${id}`);

// POS Transaction API functions
export const fetchPosTransactions = (
  searchQuery,
  page,
  pageSize = 15,
  filters = {},
) => {
  let url = `/api/pos/transactions?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}`;

  // Add filters if provided
  if (filters.status) url += `&status=${filters.status}`;
  if (filters.type) url += `&type=${filters.type}`;
  if (filters.cashierId) url += `&cashierId=${filters.cashierId}`;
  if (filters.customerId) url += `&customerId=${filters.customerId}`;
  if (filters.dateFrom) url += `&dateFrom=${filters.dateFrom}`;
  if (filters.dateTo) url += `&dateTo=${filters.dateTo}`;

  return API.get(url);
};

export const fetchPosTransaction = (id) =>
  API.get(`/api/pos/transactions/${id}`);

export const createPosTransaction = (transactionData) =>
  API.post("/api/pos/transactions", transactionData);

export const addPaymentToTransaction = (id, paymentData) =>
  API.post(`/api/pos/transactions/${id}/payment`, paymentData);

export const refundTransaction = (id, refundData) =>
  API.post(`/api/pos/transactions/${id}/refund`, refundData);

export const voidTransaction = (id, voidData) =>
  API.post(`/api/pos/transactions/${id}/void`, voidData);

// POS Reports API functions
export const fetchPosSalesReport = (startDate, endDate, groupBy = "day") => {
  return API.get(
    `/api/pos/reports/sales?startDate=${startDate}&endDate=${endDate}&groupBy=${groupBy}`,
  );
};

// Modern POS Terminal Access API functions
export const accessPosTerminal = (terminalId, accessData) =>
  API.post(`/api/pos/terminals/${terminalId}/access`, accessData);

export const logoutFromPosTerminal = () =>
  API.post("/api/pos/terminals/logout");

export const getCurrentPosSession = () =>
  API.get("/api/pos/user-session/current");

export const translate = (translationPayload) =>
  API.post(`/translation/translate`, translationPayload);

// Account Settings
export const fetchSecuritySettings = () => API.get("/settings/security");
export const updateSecuritySettings = (data) => API.put("/settings/security", data);
export const fetchModuleConfig = () => API.get("/settings/security/modules");
export const saveModuleConfig = (data) => API.put("/settings/security/modules", data);

export const fetchAccountingSettings = () => API.get(`/settings/accounting`);
export const createAccountingSettings = (newAccountingSettings) =>
  API.post(`/settings/accounting`, newAccountingSettings);
export const updateAccountingSettings = (updatedData) =>
  API.put(`/settings/accounting`, updatedData);
export const fetchAccountingPeriods = (params = {}) =>
  API.get("/settings/accounting/periods", { params });
export const closeAccountingPeriod = (period, payload = {}) =>
  API.post(`/settings/accounting/periods/${period}/close`, payload);
export const lockAccountingPeriod = (period, payload = {}) =>
  API.post(`/settings/accounting/periods/${period}/lock`, payload);
export const reopenAccountingPeriod = (period, payload = {}) =>
  API.post(`/settings/accounting/periods/${period}/reopen`, payload);

const manufacturingQuery = (params = {}) => {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")),
  );
  return query.toString() ? `?${query.toString()}` : "";
};

export const fetchManufacturingSettings = () => API.get("/manufacturing/settings");
export const updateManufacturingSettings = (data) =>
  API.patch("/manufacturing/settings", data);
export const fetchManufacturingPresets = () => API.get("/manufacturing/presets");
export const fetchManufacturingReadiness = () => API.get("/manufacturing/readiness");
export const fetchManufacturingBoms = (params = {}) => {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")),
  );
  return API.get(`/manufacturing/boms${query.toString() ? `?${query.toString()}` : ""}`);
};
export const fetchManufacturingBom = (id) => API.get(`/manufacturing/boms/${id}`);
export const createManufacturingBom = (data) => API.post("/manufacturing/boms", data);
export const updateManufacturingBom = (id, data) => API.patch(`/manufacturing/boms/${id}`, data);
export const activateManufacturingBom = (id, data = {}) => API.patch(`/manufacturing/boms/${id}/activate`, data);
export const submitManufacturingBomEngineeringApproval = (id) =>
  API.patch(`/manufacturing/boms/${id}/approval/submit`);
export const approveManufacturingBomEngineering = (id, data = {}) =>
  API.patch(`/manufacturing/boms/${id}/approval/approve`, data);
export const deactivateManufacturingBom = (id) => API.patch(`/manufacturing/boms/${id}/deactivate`);
export const deleteManufacturingBom = (id) => API.delete(`/manufacturing/boms/${id}`);
export const explodeManufacturingBom = (id, quantity = 1) =>
  API.get(`/manufacturing/boms/${id}/explode?quantity=${quantity}`);
export const rollupManufacturingBomCost = (id, quantity = 1) =>
  API.get(`/manufacturing/boms/${id}/cost-rollup?quantity=${quantity}`);
export const freezeManufacturingBomStandardCost = (id, data = {}) =>
  API.post(`/manufacturing/boms/${id}/standard-cost/freeze`, data);
export const releaseManufacturingBomStandardCost = (id, data = {}) =>
  API.post(`/manufacturing/boms/${id}/standard-cost/release`, data);
export const fetchManufacturingWorkCenters = (params = {}) => {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")),
  );
  return API.get(`/manufacturing/work-centers${query.toString() ? `?${query.toString()}` : ""}`);
};
export const createManufacturingWorkCenter = (data) =>
  API.post("/manufacturing/work-centers", data);
export const updateManufacturingWorkCenter = (id, data) =>
  API.patch(`/manufacturing/work-centers/${id}`, data);
export const activateManufacturingWorkCenter = (id) =>
  API.patch(`/manufacturing/work-centers/${id}/activate`);
export const deactivateManufacturingWorkCenter = (id) =>
  API.patch(`/manufacturing/work-centers/${id}/deactivate`);
export const fetchManufacturingWorkCenterCapacity = (params = {}) => {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")),
  );
  return API.get(`/manufacturing/work-centers/capacity${query.toString() ? `?${query.toString()}` : ""}`);
};
export const fetchManufacturingRoutings = (params = {}) => {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")),
  );
  return API.get(`/manufacturing/routings${query.toString() ? `?${query.toString()}` : ""}`);
};
export const createManufacturingRouting = (data) =>
  API.post("/manufacturing/routings", data);
export const updateManufacturingRouting = (id, data) =>
  API.patch(`/manufacturing/routings/${id}`, data);
export const activateManufacturingRouting = (id) =>
  API.patch(`/manufacturing/routings/${id}/activate`);
export const submitManufacturingRoutingEngineeringApproval = (id) =>
  API.patch(`/manufacturing/routings/${id}/approval/submit`);
export const approveManufacturingRoutingEngineering = (id, data = {}) =>
  API.patch(`/manufacturing/routings/${id}/approval/approve`, data);
export const deactivateManufacturingRouting = (id) =>
  API.patch(`/manufacturing/routings/${id}/deactivate`);
export const reviseManufacturingRouting = (id, data = {}) =>
  API.post(`/manufacturing/routings/${id}/revise`, data);
export const addManufacturingRoutingOperation = (id, data) =>
  API.post(`/manufacturing/routings/${id}/operations`, data);
export const updateManufacturingRoutingOperation = (id, operationId, data) =>
  API.patch(`/manufacturing/routings/${id}/operations/${operationId}`, data);
export const removeManufacturingRoutingOperation = (id, operationId) =>
  API.delete(`/manufacturing/routings/${id}/operations/${operationId}`);
export const reorderManufacturingRoutingOperations = (id, data) =>
  API.patch(`/manufacturing/routings/${id}/operations/reorder`, data);
export const submitManufacturingRoutingDocumentApproval = (id, operationId, documentId) =>
  API.patch(`/manufacturing/routings/${id}/operations/${operationId}/documents/${documentId}/approval/submit`);
export const approveManufacturingRoutingDocument = (id, operationId, documentId, data = {}) =>
  API.patch(`/manufacturing/routings/${id}/operations/${operationId}/documents/${documentId}/approval/approve`, data);
export const fetchManufacturingWorkOrders = (params = {}) => {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")),
  );
  return API.get(`/manufacturing/work-orders${query.toString() ? `?${query.toString()}` : ""}`);
};
export const fetchManufacturingWorkOrder = (id) =>
  API.get(`/manufacturing/work-orders/${id}`);
export const fetchManufacturingWorkOrderCost = (id) =>
  API.get(`/manufacturing/work-orders/${id}/cost`);
export const createManufacturingWorkOrder = (data) =>
  API.post("/manufacturing/work-orders", data);
export const updateManufacturingWorkOrder = (id, data) =>
  API.patch(`/manufacturing/work-orders/${id}`, data);
export const confirmManufacturingWorkOrder = (id) =>
  API.patch(`/manufacturing/work-orders/${id}/confirm`);
export const generateManufacturingWorkOrderQrLabel = (id, data = {}) =>
  API.post(`/manufacturing/work-orders/${id}/qr-label`, data);
export const generateManufacturingWorkOrderLotSerialQrLabels = (id, data = {}) =>
  API.post(`/manufacturing/work-orders/${id}/lot-serial-labels`, data);
export const startManufacturingWorkOrder = (id) =>
  API.patch(`/manufacturing/work-orders/${id}/start`);
export const pauseManufacturingWorkOrder = (id) =>
  API.patch(`/manufacturing/work-orders/${id}/pause`);
export const resumeManufacturingWorkOrder = (id) =>
  API.patch(`/manufacturing/work-orders/${id}/resume`);
export const recordManufacturingWorkOrderProduction = (id, data) =>
  API.post(`/manufacturing/work-orders/${id}/production`, data);
export const attachManufacturingWorkOrderOperationDocument = (id, operationId, data = {}) =>
  API.post(`/manufacturing/work-orders/${id}/operations/${operationId}/documents`, data);
export const reserveManufacturingWorkOrderMaterials = (id, data = {}) =>
  API.post(`/manufacturing/work-orders/${id}/reserve-materials`, data);
export const issueManufacturingWorkOrderMaterials = (id, data = {}) =>
  API.post(`/manufacturing/work-orders/${id}/issue-materials`, data);
export const returnManufacturingWorkOrderMaterials = (id, data = {}) =>
  API.post(`/manufacturing/work-orders/${id}/return-materials`, data);
export const receiveManufacturingWorkOrderFinishedGoods = (id, data = {}) =>
  API.post(`/manufacturing/work-orders/${id}/receive-finished-goods`, data);
export const receiveManufacturingWorkOrderByproducts = (id, data = {}) =>
  API.post(`/manufacturing/work-orders/${id}/receive-byproducts`, data);
export const postManufacturingWorkOrderScrap = (id, data = {}) =>
  API.post(`/manufacturing/work-orders/${id}/post-scrap`, data);
export const closeManufacturingWorkOrder = (id, data = {}) =>
  API.patch(`/manufacturing/work-orders/${id}/close`, data);
export const reverseManufacturingWorkOrderCosting = (id, data = {}) =>
  API.patch(`/manufacturing/work-orders/${id}/reverse-costing`, data);
export const cancelManufacturingWorkOrder = (id, data = {}) =>
  API.patch(`/manufacturing/work-orders/${id}/cancel`, data);
export const fetchManufacturingQcTemplates = (params = {}) => {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")),
  );
  return API.get(`/manufacturing/qc/templates${query.toString() ? `?${query.toString()}` : ""}`);
};
export const fetchManufacturingQcTemplate = (id) =>
  API.get(`/manufacturing/qc/templates/${id}`);
export const createManufacturingQcTemplate = (data) =>
  API.post("/manufacturing/qc/templates", data);
export const updateManufacturingQcTemplate = (id, data) =>
  API.patch(`/manufacturing/qc/templates/${id}`, data);
export const activateManufacturingQcTemplate = (id) =>
  API.patch(`/manufacturing/qc/templates/${id}/activate`);
export const fetchManufacturingQcInspections = (params = {}) => {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")),
  );
  return API.get(`/manufacturing/qc/inspections${query.toString() ? `?${query.toString()}` : ""}`);
};
export const fetchManufacturingQcInspection = (id) =>
  API.get(`/manufacturing/qc/inspections/${id}`);
export const fetchManufacturingQcCertificateOfAnalysis = (id, params = {}) => {
  const query = new URLSearchParams(params);
  return API.get(`/manufacturing/qc/inspections/${id}/certificate-of-analysis${query.toString() ? `?${query.toString()}` : ""}`);
};
export const fetchManufacturingQcCertificateOfAnalysisCsv = (id, params = {}) => {
  const query = new URLSearchParams({ ...params, format: "csv" });
  return API.get(`/manufacturing/qc/inspections/${id}/certificate-of-analysis?${query.toString()}`, {
    responseType: "blob",
  });
};
export const fetchManufacturingQcCertificateOfAnalysisPdf = (id, params = {}) => {
  const query = new URLSearchParams({ ...params, format: "pdf" });
  return API.get(`/manufacturing/qc/inspections/${id}/certificate-of-analysis?${query.toString()}`, {
    responseType: "blob",
  });
};
export const createManufacturingQcInspection = (data) =>
  API.post("/manufacturing/qc/inspections", data);
export const updateManufacturingQcInspection = (id, data) =>
  API.patch(`/manufacturing/qc/inspections/${id}`, data);
export const submitManufacturingQcInspection = (id, data = {}) =>
  API.patch(`/manufacturing/qc/inspections/${id}/submit`, data);
export const dispositionManufacturingQcInspection = (id, data = {}) =>
  API.patch(`/manufacturing/qc/inspections/${id}/disposition`, data);
export const fetchManufacturingQcNonConformances = (params = {}) => {
  const query = new URLSearchParams(params);
  return API.get(`/manufacturing/qc/non-conformances${query.toString() ? `?${query.toString()}` : ""}`);
};
export const createManufacturingQcNonConformanceFromInspection = (inspectionId, data = {}) =>
  API.post(`/manufacturing/qc/inspections/${inspectionId}/non-conformance`, data);
export const dispositionManufacturingQcNonConformance = (id, data = {}) =>
  API.patch(`/manufacturing/qc/non-conformances/${id}/disposition`, data);
export const fetchManufacturingQcCapas = (params = {}) => {
  const query = new URLSearchParams(params);
  return API.get(`/manufacturing/qc/capas${query.toString() ? `?${query.toString()}` : ""}`);
};
export const updateManufacturingQcCapa = (id, data = {}) =>
  API.patch(`/manufacturing/qc/capas/${id}`, data);
export const fetchManufacturingProductionPlans = (params = {}) => {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")),
  );
  return API.get(`/manufacturing/plans${query.toString() ? `?${query.toString()}` : ""}`);
};
export const fetchManufacturingProductionPlan = (id) =>
  API.get(`/manufacturing/plans/${id}`);
export const createManufacturingProductionPlan = (data) =>
  API.post("/manufacturing/plans", data);
export const updateManufacturingProductionPlan = (id, data) =>
  API.patch(`/manufacturing/plans/${id}`, data);
export const addManufacturingProductionPlanLine = (id, data) =>
  API.post(`/manufacturing/plans/${id}/lines`, data);
export const updateManufacturingProductionPlanLine = (id, lineId, data) =>
  API.patch(`/manufacturing/plans/${id}/lines/${lineId}`, data);
export const removeManufacturingProductionPlanLine = (id, lineId) =>
  API.delete(`/manufacturing/plans/${id}/lines/${lineId}`);
export const approveManufacturingProductionPlan = (id) =>
  API.patch(`/manufacturing/plans/${id}/approve`);
export const reopenManufacturingProductionPlan = (id) =>
  API.patch(`/manufacturing/plans/${id}/reopen`);
export const closeManufacturingProductionPlan = (id) =>
  API.patch(`/manufacturing/plans/${id}/close`);
export const generateManufacturingProductionPlanWorkOrders = (id) =>
  API.post(`/manufacturing/plans/${id}/generate-work-orders`);
export const fetchManufacturingProductionPlanShortages = (id) =>
  API.get(`/manufacturing/plans/${id}/shortages`);
export const fetchManufacturingProductionPlanCapacity = (id) =>
  API.get(`/manufacturing/plans/${id}/capacity`);
export const importManufacturingProductionPlanForecast = (data = {}) =>
  API.post("/manufacturing/plans/import-forecast", data);
export const runManufacturingProductionMrp = (data = {}) =>
  API.post("/manufacturing/plans/mrp-run", data);
export const fetchManufacturingSubcontractOrders = (params = {}) => {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")),
  );
  return API.get(`/manufacturing/subcontract-orders${query.toString() ? `?${query.toString()}` : ""}`);
};
export const fetchManufacturingSubcontractOrder = (id) =>
  API.get(`/manufacturing/subcontract-orders/${id}`);
export const createManufacturingSubcontractOrder = (data) =>
  API.post("/manufacturing/subcontract-orders", data);
export const updateManufacturingSubcontractOrder = (id, data) =>
  API.patch(`/manufacturing/subcontract-orders/${id}`, data);
export const sendManufacturingSubcontractOrder = (id, data = {}) =>
  API.patch(`/manufacturing/subcontract-orders/${id}/send`, data);
export const receiveManufacturingSubcontractOrder = (id, data = {}) =>
  API.patch(`/manufacturing/subcontract-orders/${id}/receive`, data);
export const closeManufacturingSubcontractOrder = (id, data = {}) =>
  API.patch(`/manufacturing/subcontract-orders/${id}/close`, data);
export const cancelManufacturingSubcontractOrder = (id, data = {}) =>
  API.patch(`/manufacturing/subcontract-orders/${id}/cancel`, data);
export const fetchManufacturingMaintenanceRequests = (params = {}) => {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")),
  );
  return API.get(`/manufacturing/maintenance${query.toString() ? `?${query.toString()}` : ""}`);
};
export const fetchManufacturingMaintenanceRequest = (id) =>
  API.get(`/manufacturing/maintenance/${id}`);
export const createManufacturingMaintenanceRequest = (data) =>
  API.post("/manufacturing/maintenance", data);
export const updateManufacturingMaintenanceRequest = (id, data) =>
  API.patch(`/manufacturing/maintenance/${id}`, data);
export const scheduleManufacturingMaintenanceRequest = (id, data = {}) =>
  API.patch(`/manufacturing/maintenance/${id}/schedule`, data);
export const startManufacturingMaintenanceRequest = (id) =>
  API.patch(`/manufacturing/maintenance/${id}/start`);
export const completeManufacturingMaintenanceRequest = (id, data = {}) =>
  API.patch(`/manufacturing/maintenance/${id}/complete`, data);
export const cancelManufacturingMaintenanceRequest = (id, data = {}) =>
  API.patch(`/manufacturing/maintenance/${id}/cancel`, data);
export const generateManufacturingPreventiveMaintenance = (data = {}) =>
  API.post("/manufacturing/maintenance/preventive-generate", data);
export const fetchManufacturingWorkCenterOee = (workCenterId, params = {}) => {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")),
  );
  return API.get(`/manufacturing/maintenance/oee/${workCenterId}${query.toString() ? `?${query.toString()}` : ""}`);
};
export const fetchManufacturingDashboardReport = (params = {}) =>
  API.get(`/manufacturing/reports/dashboard${manufacturingQuery(params)}`);
export const fetchManufacturingProductionSummaryReport = (params = {}) =>
  API.get(`/manufacturing/reports/production-summary${manufacturingQuery(params)}`);
export const fetchManufacturingWorkCenterUtilizationReport = (params = {}) =>
  API.get(`/manufacturing/reports/work-center-utilization${manufacturingQuery(params)}`);
export const fetchManufacturingMaintenanceLogReport = (params = {}) =>
  API.get(`/manufacturing/reports/maintenance-log${manufacturingQuery(params)}`);
export const fetchManufacturingMaterialConsumptionReport = (params = {}) =>
  API.get(`/manufacturing/reports/material-consumption${manufacturingQuery(params)}`);
export const fetchManufacturingCostVarianceReport = (params = {}) =>
  API.get(`/manufacturing/reports/cost-variance${manufacturingQuery(params)}`);
export const fetchManufacturingScrapYieldReport = (params = {}) =>
  API.get(`/manufacturing/reports/scrap-yield${manufacturingQuery(params)}`);
export const fetchManufacturingQcSummaryReport = (params = {}) =>
  API.get(`/manufacturing/reports/qc-summary${manufacturingQuery(params)}`);
export const fetchManufacturingSubcontractingStatusReport = (params = {}) =>
  API.get(`/manufacturing/reports/subcontracting-status${manufacturingQuery(params)}`);
export const fetchManufacturingWipAgingReport = (params = {}) =>
  API.get(`/manufacturing/reports/wip-aging${manufacturingQuery(params)}`);
export const fetchManufacturingLotSerialGenealogyReport = (params = {}) =>
  API.get(`/manufacturing/reports/lot-serial-genealogy${manufacturingQuery(params)}`);
export const fetchManufacturingSubledgerReconciliationReport = (params = {}) =>
  API.get(`/manufacturing/reports/subledger-reconciliation${manufacturingQuery(params)}`);
export const fetchManufacturingControlExceptionsReport = (params = {}) =>
  API.get(`/manufacturing/reports/control-exceptions${manufacturingQuery(params)}`);

// Properties
export const fetchProperties = (filters = {}, page = 1, pageSize = 10) => {
  const queryParams = new URLSearchParams();
  if (filters.propertyType)
    queryParams.append("propertyType", filters.propertyType);
  if (filters.listingPurpose)
    queryParams.append("listingPurpose", filters.listingPurpose);
  if (filters.propertyStatus)
    queryParams.append("propertyStatus", filters.propertyStatus);
  if (filters.minPrice) queryParams.append("minPrice", filters.minPrice);
  if (filters.maxPrice) queryParams.append("maxPrice", filters.maxPrice);
  if (filters.city) queryParams.append("city", filters.city);
  queryParams.append("page", page);
  queryParams.append("pageSize", pageSize);

  return API.get(`/properties?${queryParams.toString()}`);
};

export const fetchProperty = (id) => API.get(`/properties/${id}`);
export const createProperty = (propertyData) =>
  API.post("/properties", propertyData);
export const updateProperty = (id, propertyData) =>
  API.patch(`/properties/${id}`, propertyData);
export const deleteProperty = (id) => API.delete(`/properties/${id}`);
export const searchProperties = (
  searchParams = {},
  page = 1,
  pageSize = 10,
) => {
  const queryParams = new URLSearchParams();
  if (searchParams.searchTerm)
    queryParams.append("searchTerm", searchParams.searchTerm);
  if (searchParams.propertyType)
    queryParams.append("propertyType", searchParams.propertyType);
  if (searchParams.minPrice)
    queryParams.append("minPrice", searchParams.minPrice);
  if (searchParams.maxPrice)
    queryParams.append("maxPrice", searchParams.maxPrice);
  if (searchParams.city) queryParams.append("city", searchParams.city);
  if (searchParams.listingPurpose)
    queryParams.append("listingPurpose", searchParams.listingPurpose);
  queryParams.append("page", page);
  queryParams.append("pageSize", pageSize);

  return API.get(`/properties/search?${queryParams.toString()}`);
};

// Properties
export const fetchNewProperties = () => API.get(`/properties`);
export const fetchPropertyById = (id) => API.get(`/properties/${id}`);
export const createNewProperty = (data) => API.post(`/properties`, data);
export const updateProperties = (id, data) =>
  API.patch(`/properties/${id}`, data);
export const deleteProperties = (id) => API.delete(`/properties/${id}`);
export const deleteAttachment = (id, fileId) =>
  API.delete(`/properties/${id}/attachments/${fileId}`);
export const addAttachments = (id, data) =>
  API.post(`/properties/${id}/attachments`, data);
export const uploadAttachments = (data) => API.post(`/upload/multiple`, data);

// Messaging API
export const fetchConversations = () => API.get("/messaging/conversations");
export const fetchConversation = (id) =>
  API.get(`/messaging/conversations/${id}`);
export const createConversation = (conversationData) =>
  API.post("/messaging/conversations", conversationData);
export const updateConversation = (id, updateData) =>
  API.patch(`/messaging/conversations/${id}`, updateData);
export const deleteConversation = (id) =>
  API.delete(`/messaging/conversations/${id}`);
export const fetchMessages = (conversationId, page = 1, limit = 50) =>
  API.get(
    `/messaging/conversations/${conversationId}/messages?page=${page}&limit=${limit}`,
  );
export const sendMessage = (conversationId, messageData) =>
  API.post(`/messaging/messages`, messageData);
export const deleteMessage = (conversationId, messageId) =>
  API.delete(`/messaging/messages/${messageId}`);
export const addParticipant = (conversationId, userData) =>
  API.post(`/messaging/conversations/${conversationId}/participants`, userData);
export const removeParticipant = (conversationId, userId) =>
  API.delete(
    `/messaging/conversations/${conversationId}/participants/${userId}`,
  );
export const fetchUnreadCount = () =>
  API.get("/messaging/messages/unread/count");
export const markAsRead = (conversationId) =>
  API.put(`/messaging/messages/${conversationId}/read`);
export const uploadMessageAttachment = (conversationId, formData) =>
  API.post(`/messaging/conversations/${conversationId}/attachments`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
export const deleteMessageAttachment = (conversationId, attachmentId) =>
  API.delete(
    `/messaging/conversations/${conversationId}/attachments/${attachmentId}`,
  );

// Real Users API for Messaging
export const fetchMessagingUsers = (searchQuery = "", page = 1, pageSize = 500) =>
  API.get(`/users?search=${encodeURIComponent(searchQuery)}&page=${page}&pageSize=${pageSize}`);
export const fetchMessagingUserById = (userId) => API.get(`/users/${userId}`);

// Inventory Management API
export const fetchInventory = (searchQuery, page = 1, pageSize = 15) => {
  const searchTerm =
    searchQuery && typeof searchQuery.search === "string"
      ? searchQuery.search
      : "";
  return API.get(
    `/inventory?searchQuery=${searchTerm}&page=${page}&pageSize=${pageSize}`,
  );
};
export const fetchInventoryItem = (id) => API.get(`/inventory/record/${id}`);
export const addInventoryItem = (inventoryItem) =>
  API.post("/inventory", inventoryItem);
export const updateInventoryItem = (id, updatedInventoryItem) =>
  API.patch(`/inventory/${id}`, updatedInventoryItem);
export const deleteInventoryItem = (id) => API.delete(`/inventory/${id}`);
export const fetchInventoryAlerts = (params = {}) =>
  API.get("/inventory/alerts", { params });
export const fetchProductInventory = (productId, warehouseId) =>
  API.get(
    `/inventory/products/${productId}${warehouseId ? `?warehouse=${warehouseId}` : ""
    }`,
  );

// Unit of Measure API
export const fetchUnitOfMeasures = (searchQuery, page = 1, pageSize = 15) =>
  API.get(
    `/units-of-measure?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}&selector=${searchQuery.selector || ""}`,
  );
export const fetchUnitOfMeasure = (id) => API.get(`/units-of-measure/${id}`);
export const addUnitOfMeasure = (unitOfMeasure) =>
  API.post("/units-of-measure", unitOfMeasure);
export const updateUnitOfMeasure = (id, updatedUnitOfMeasure) =>
  API.patch(`/units-of-measure/${id}`, updatedUnitOfMeasure);
export const deleteUnitOfMeasure = (id) =>
  API.delete(`/units-of-measure/${id}`);
export const fetchActiveUnitOfMeasures = () =>
  API.get("/units-of-measure/active");
export const fetchUnitOfMeasuresByCategory = (category) =>
  API.get(`/units-of-measure/category/${category}`);
export const convertUnitOfMeasure = (fromId, toId, quantity) =>
  API.get(
    `/units-of-measure/convert?fromId=${fromId}&toId=${toId}&quantity=${quantity}`,
  );

// Warehouse API
export const fetchWarehouses = (searchQuery, page = 1, pageSize = 15) =>
  API.get(
    `/warehouses?searchQuery=${searchQuery.search}&page=${page}&pageSize=${pageSize}&selector=${searchQuery.selector || ""}`,
  );
export const fetchWarehouse = (id) => API.get(`/warehouses/${id}`);
export const addWarehouse = (warehouse) => API.post("/warehouses", warehouse);
export const updateWarehouse = (id, updatedWarehouse) =>
  API.patch(`/warehouses/${id}`, updatedWarehouse);
export const deleteWarehouse = (id) => API.delete(`/warehouses/${id}`);

// Inventory Adjustments API
export const fetchInventoryAdjustments = (
  filters = {},
  page = 1,
  pageSize = 15,
) => {
  let queryParams = `page=${page}&limit=${pageSize}`;

  if (filters.warehouse) queryParams += `&warehouse=${filters.warehouse}`;
  if (filters.status) queryParams += `&status=${filters.status}`;
  if (filters.startDate) queryParams += `&startDate=${filters.startDate}`;
  if (filters.endDate) queryParams += `&endDate=${filters.endDate}`;
  if (filters.search) queryParams += `&searchQuery=${filters.search}`;

  return API.get(`/inventory/adjustments?${queryParams}`);
};
export const fetchInventoryAdjustment = (id) =>
  API.get(`/inventory/adjustments/${id}`);
export const addInventoryAdjustment = (adjustment) =>
  API.post("/inventory/adjustments", adjustment);
export const updateInventoryAdjustment = (id, updatedAdjustment) =>
  API.patch(`/inventory/adjustments/${id}`, updatedAdjustment);
export const postInventoryAdjustment = (id, payload = {}) =>
  API.post(`/inventory/adjustments/${id}/post`, payload);
export const deleteInventoryAdjustment = (id) =>
  API.delete(`/inventory/adjustments/${id}`);

// Inventory Transactions API
export const fetchInventoryTransactions = (
  filters = {},
  page = 1,
  pageSize = 15,
) => {
  let queryParams = `page=${page}&limit=${pageSize}`;

  if (filters.transactionType)
    queryParams += `&transactionType=${filters.transactionType}`;
  if (filters.status) queryParams += `&status=${filters.status}`;
  if (filters.startDate) queryParams += `&startDate=${filters.startDate}`;
  if (filters.endDate) queryParams += `&endDate=${filters.endDate}`;
  if (filters.search) queryParams += `&searchQuery=${filters.search}`;

  return API.get(`/inventory/transactions?${queryParams}`);
};
export const fetchInventoryTransaction = (id) =>
  API.get(`/inventory/transactions/${id}`);
export const createInventoryTransfer = (transfer) =>
  API.post("/inventory/transfer", transfer);
export const fetchInventoryValuation = () => API.get("/inventory/valuation");
export const fetchInventoryValuationReport = (params = {}) => {
  const q = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v)),
  ).toString();
  return API.get(`/inventory/valuation-report${q ? `?${q}` : ""}`);
};

// Company Settings
export const getCompanySettings = () => API.get("/company-profile");
export const updateCompanySettings = (updatedSettings) =>
  API.patch("/company-profile", updatedSettings);

export const getZatcaSettings = () =>
  API.get("/api/integration/zatca/settings");
export const updateZatcaSettings = (updatedSettings) =>
  API.patch("/api/integration/zatca/settings", updatedSettings);
export const getZatcaStatus = () => API.get("/api/integration/zatca/status");
export const generateZatcaCSR = (payload) =>
  API.post("/api/integration/zatca/csr", payload);
export const getZatcaCertificateInfo = (vatNumber, companyId) =>
  API.get(
    `/api/integration/zatca/certificates/${vatNumber}${companyId ? `?companyId=${companyId}` : ""
    }`,
  );
export const onboardZatca = (payload) =>
  API.post("/api/integration/zatca/onboard", payload);
export const requestZatcaProductionCsid = (payload) =>
  API.post("/api/integration/zatca/production-csid", payload);
export const runZatcaComplianceInvoice = (payload) =>
  API.post("/api/integration/zatca/compliance/invoice", payload);
export const reportZatcaInvoice = (payload) =>
  API.post("/api/integration/zatca/report", payload);
export const clearZatcaInvoice = (payload) =>
  API.post("/api/integration/zatca/clear", payload);
export const runZatcaTestCycle = (payload) =>
  API.post("/api/integration/zatca/test-cycle", payload);

// Product Attributes
export const fetchAttributes = (searchQuery, page = 1, pageSize = 15) => {
  let queryParams = new URLSearchParams();

  if (searchQuery && searchQuery.search) {
    queryParams.append("search", searchQuery.search);
  }

  if (page) {
    queryParams.append("page", page);
  }

  if (pageSize) {
    queryParams.append("limit", pageSize);
  }

  return API.get(`/attributes?${queryParams}`);
};

export const fetchAttribute = (id) => API.get(`/attributes/${id}`);
export const createAttribute = (attribute) =>
  API.post("/attributes", attribute);
export const updateAttribute = (id, updatedAttribute) =>
  API.patch(`/attributes/${id}`, updatedAttribute);
export const deleteAttribute = (id) => API.delete(`/attributes/${id}`);

export const fetchAttributeValues = (attributeId) =>
  API.get(`/attributes/${attributeId}/values`);
export const createAttributeValue = (attributeId, value) =>
  API.post(`/attributes/${attributeId}/values`, value);
export const updateAttributeValue = (attributeId, valueId, updatedValue) =>
  API.patch(`/attributes/${attributeId}/values/${valueId}`, updatedValue);
export const deleteAttributeValue = (attributeId, valueId) =>
  API.delete(`/attributes/${attributeId}/values/${valueId}`);

// Clinic API Functions

// Doctor API calls
export const fetchDoctors = (filters = {}) => {
  const { searchQuery = "", specialization = "", isActive } = filters;
  const queryParams = new URLSearchParams();

  if (searchQuery) queryParams.append("searchQuery", searchQuery);
  if (isActive !== undefined && isActive !== "All") {
    queryParams.append("isActive", isActive);
  }
  if (specialization !== undefined && specialization !== "") {
    queryParams.append("specialization", specialization);
  }

  const queryString = queryParams.toString();
  const url = queryString ? `/doctors?${queryString}` : "/doctors";
  return API.get(url);
};

export const fetchDoctor = (id) => API.get(`/doctors/${id}`);
export const createDoctor = (doctorData) => API.post("/doctors", doctorData);
export const updateDoctor = (id, doctorData) =>
  API.put(`/doctors/${id}`, doctorData);
export const deleteDoctor = (id) => API.delete(`/doctors/${id}`);

// Patient API calls
export const fetchPatients = (filters = {}) => {
  const { searchQuery = "", gender = "", bloodGroup = "", isActive } = filters;
  const queryParams = new URLSearchParams();

  if (searchQuery) queryParams.append("searchQuery", searchQuery);
  if (gender !== undefined && gender !== "All") {
    queryParams.append("gender", gender);
  }
  if (bloodGroup !== undefined && bloodGroup !== "All") {
    queryParams.append("bloodGroup", bloodGroup);
  }
  if (isActive !== undefined && isActive !== "All") {
    queryParams.append("isActive", isActive);
  }

  const queryString = queryParams.toString();
  const url = queryString ? `/patients?${queryString}` : "/patients";

  return API.get(url);
};

export const fetchPatient = (id) => API.get(`/patients/${id}`);
export const createPatient = (patientData) =>
  API.post("/patients", patientData);
export const updatePatient = (id, patientData) =>
  API.put(`/patients/${id}`, patientData);
export const deletePatient = (id) => API.delete(`/patients/${id}`);

// Appointment API calls
export const getAppointments = () => API.get("/appointments");
export const getAppointmentsByFilters = (filters = {}) => {
  const {
    searchQuery = "",
    status = "",
    date = "",
    doctorId = "",
    patientId = "",
  } = filters;
  const queryParams = new URLSearchParams();

  if (searchQuery) queryParams.append("searchQuery", searchQuery);
  if (status !== undefined && status !== "All") {
    queryParams.append("status", status);
  }
  if (date) queryParams.append("date", date);
  if (doctorId) queryParams.append("doctorId", doctorId);
  if (patientId) queryParams.append("patientId", patientId);

  const queryString = queryParams.toString();
  const url = queryString ? `/appointments?${queryString}` : "/appointments";

  return API.get(url);
};

export const getAppointment = (id) => API.get(`/appointments/${id}`);
export const getAppointmentsByPatient = (patientId, filters = {}) => {
  const { status = "", startDate = "", endDate = "" } = filters;
  const queryParams = new URLSearchParams();

  if (status) queryParams.append("status", status);
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);

  const queryString = queryParams.toString();
  const url = queryString
    ? `/appointments/patient/${patientId}?${queryString}`
    : `/appointments/patient/${patientId}`;

  return API.get(url);
};
export const getAppointmentsByDoctor = (doctorId, filters = {}) => {
  const { status = "", startDate = "", endDate = "" } = filters;
  const queryParams = new URLSearchParams();

  if (status) queryParams.append("status", status);
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);

  const queryString = queryParams.toString();
  const url = queryString
    ? `/appointments/doctor/${doctorId}?${queryString}`
    : `/appointments/doctor/${doctorId}`;

  return API.get(url);
};
export const createAppointment = (appointmentData) =>
  API.post("/appointments", appointmentData);
export const updateAppointment = (id, appointmentData) =>
  API.put(`/appointments/${id}`, appointmentData);
export const deleteAppointment = (id) => API.delete(`/appointments/${id}`);

// Lab Result API calls
export const fetchLabResults = () => API.get("/lab-results");
export const fetchLabResult = (id) => API.get(`/lab-results/${id}`);
export const createLabResult = (labResultData) =>
  API.post("/lab-results", labResultData);
export const updateLabResult = (id, labResultData) =>
  API.put(`/lab-results/${id}`, labResultData);
export const deleteLabResult = (id) => API.delete(`/lab-results/${id}`);

// Lab Test API calls
export const fetchLabTests = () => API.get("/lab-tests");
export const fetchLabTest = (id) => API.get(`/lab-tests/${id}`);
export const createLabTest = (labTestData) =>
  API.post("/lab-tests", labTestData);
export const updateLabTest = (id, labTestData) =>
  API.put(`/lab-tests/${id}`, labTestData);
export const deleteLabTest = (id) => API.delete(`/lab-tests/${id}`);

// Medical Record API calls
export const fetchMedicalRecords = () => API.get("/medical-records");
export const fetchMedicalRecord = (id) => API.get(`/medical-records/${id}`);
export const createMedicalRecord = (medicalRecordData) =>
  API.post("/medical-records", medicalRecordData);
export const updateMedicalRecord = (id, medicalRecordData) =>
  API.put(`/medical-records/${id}`, medicalRecordData);
export const deleteMedicalRecord = (id) => API.delete(`/medical-records/${id}`);

// Medical Service API calls
export const getMedicalServices = () => API.get("/medical-services");
export const getMedicalService = (id) => API.get(`/medical-services/${id}`);
export const createMedicalService = (medicalServiceData) =>
  API.post("/medical-services", medicalServiceData);
export const updateMedicalService = (id, medicalServiceData) =>
  API.put(`/medical-services/${id}`, medicalServiceData);
export const deleteMedicalService = (id) =>
  API.delete(`/medical-services/${id}`);

// Medication API calls
export const getMedications = () => API.get("/medications");
export const getMedication = (id) => API.get(`/medications/${id}`);
export const createMedication = (medicationData) =>
  API.post("/medications", medicationData);
export const updateMedication = (id, medicationData) =>
  API.put(`/medications/${id}`, medicationData);
export const deleteMedication = (id) => API.delete(`/medications/${id}`);

// Prescription API calls
export const getPrescriptions = () => API.get("/prescriptions");
export const getPrescription = (id) => API.get(`/prescriptions/${id}`);
export const createPrescription = (prescriptionData) =>
  API.post("/prescriptions", prescriptionData);
export const updatePrescription = (id, prescriptionData) =>
  API.put(`/prescriptions/${id}`, prescriptionData);
export const deletePrescription = (id) => API.delete(`/prescriptions/${id}`);
export const updatePrescriptionStatus = (id, status, note) =>
  API.patch(`/prescriptions/${id}/status`, { status, note });

// Journal APIs
// Journal management
export const getJournals = (params) => API.get("/journals", { params });
export const createJournal = (journalData) =>
  API.post("/journals", journalData);
export const getJournal = (id) => API.get(`/journals/${id}`);
export const updateJournal = (id, journalData) =>
  API.put(`/journals/${id}`, journalData);
export const deleteJournal = (id) => API.delete(`/journals/${id}`);
export const getJournalSummary = (params) =>
  API.get("/journals/summary", { params });

// Journal entries
export const getJournalEntries = (params) =>
  API.get("/journals/entries", { params });
export const createJournalEntry = (journalData) =>
  API.post("/journals/entries", journalData);
export const getJournalEntry = (id) => API.get(`/journals/entries/${id}`);
export const updateJournalEntry = (id, journalData) =>
  API.put(`/journals/entries/${id}`, journalData);
export const deleteJournalEntry = (id) => API.delete(`/journals/entries/${id}`);

// Journal entry workflow
export const submitJournalEntry = (id) =>
  API.post(`/journals/entries/${id}/submit`);
export const approveJournalEntry = (id) =>
  API.post(`/journals/entries/${id}/approve`);
export const postJournalEntry = (id) =>
  API.post(`/journals/entries/${id}/post`);
export const reverseJournalEntry = (id, data) =>
  API.post(`/journals/entries/${id}/reverse`, data);

// Bulk operations
export const bulkApproveEntries = (ids) =>
  API.post("/journals/entries/bulk/approve", { ids });
export const bulkPostEntries = (ids) =>
  API.post("/journals/entries/bulk/post", { ids });

// Reports
export const getTrialBalance = (params) =>
  API.get("/reports/trial-balance", { params });
export const getJournalReport = (params) =>
  API.get("/reports/journal", { params });

// Modern Attendance APIs
export const fetchModernAttendance = (
  searchQuery = {},
  page = 1,
  pageSize = 15,
) => {
  const queryParams = new URLSearchParams();
  if (searchQuery.search) queryParams.append("searchQuery", searchQuery.search);
  if (searchQuery.status) queryParams.append("status", searchQuery.status);
  if (searchQuery.userId) queryParams.append("userId", searchQuery.userId);
  if (searchQuery.startDate)
    queryParams.append("startDate", searchQuery.startDate);
  if (searchQuery.endDate) queryParams.append("endDate", searchQuery.endDate);
  queryParams.append("page", page);
  queryParams.append("limit", pageSize); // Backend expects 'limit' not 'pageSize'
  return API.get(`/attendance?${queryParams.toString()}`);
};

export const fetchAttendanceById = (id) => API.get(`/attendance/${id}`);
export const checkIn = (attendanceData) =>
  API.post("/attendance/checkin", attendanceData);
export const checkOut = (attendanceData) =>
  API.post("/attendance/checkout", attendanceData);
export const createAttendanceRecord = (attendanceData) =>
  API.post("/attendance", attendanceData);
export const updateAttendanceRecord = (id, attendanceData) =>
  API.patch(`/attendance/${id}`, attendanceData);
export const deleteAttendanceRecord = (id) => API.delete(`/attendance/${id}`);

// Shift Management APIs
export const fetchShifts = (searchQuery = {}, page = 1, pageSize = 15) => {
  const queryParams = new URLSearchParams();
  if (searchQuery.search) queryParams.append("searchQuery", searchQuery.search);
  if (searchQuery.isActive !== undefined)
    queryParams.append("isActive", searchQuery.isActive);
  queryParams.append("page", page);
  queryParams.append("pageSize", pageSize);
  return API.get(`/shifts?${queryParams.toString()}`);
};

export const fetchShift = (id) => API.get(`/shifts/${id}`);
export const createShift = (shiftData) => API.post("/shifts", shiftData);
export const updateShift = (id, shiftData) =>
  API.patch(`/shifts/${id}`, shiftData);
export const deleteShift = (id) => API.delete(`/shifts/${id}`);

// Holiday Management APIs
export const fetchHolidays = (searchQuery = {}, page = 1, pageSize = 15) => {
  const queryParams = new URLSearchParams();
  if (searchQuery.search) queryParams.append("searchQuery", searchQuery.search);
  if (searchQuery.year) queryParams.append("year", searchQuery.year);
  if (searchQuery.type) queryParams.append("type", searchQuery.type);
  queryParams.append("page", page);
  queryParams.append("pageSize", pageSize);
  return API.get(`/holidays?${queryParams.toString()}`);
};

export const fetchHoliday = (id) => API.get(`/holidays/${id}`);
export const createHoliday = (holidayData) =>
  API.post("/holidays", holidayData);
export const updateHoliday = (id, holidayData) =>
  API.patch(`/holidays/${id}`, holidayData);
export const deleteHoliday = (id) => API.delete(`/holidays/${id}`);

// Attendance Configuration APIs
export const fetchAttendanceConfig = () => API.get("/attendance/config");
export const updateAttendanceConfig = (configData) =>
  API.post("/attendance/config", configData);

// Subscription APIs
export const createPaymentIntent = (paymentData) =>
  API.post("/subscriptions/create-payment-intent", paymentData);
export const confirmPayment = (confirmationData) =>
  API.post("/subscriptions/confirm-payment", confirmationData);
export const fetchSubscription = (id) => API.get(`/subscriptions/${id}`);
export const getMySubscription = () =>
  API.get("/subscriptions/my-subscription");
export const updatePaymentMethod = (paymentMethodData) =>
  API.post("/subscriptions/update-payment-method", paymentMethodData);
export const cancelSubscription = (subscriptionId, cancellationData) =>
  API.delete(`/subscriptions/${subscriptionId}/cancel`, {
    data: cancellationData,
  });

// Download subscription payment invoice
export const downloadSubscriptionInvoice = (paymentId, lang = "en") =>
  API.get(`/subscriptions/payment/${paymentId}/invoice/${lang}`);

// Additional Subscription APIs
export const getAvailablePlans = (currentPackageId) =>
  API.get(`/subscriptions/available-plans/${currentPackageId}`);
export const changePlan = (subscriptionId, changeData) =>
  API.post(`/subscriptions/${subscriptionId}/change-plan`, changeData);
export const createPlanChangePaymentIntent = (subscriptionId, changeData) =>
  API.post(
    `/subscriptions/${subscriptionId}/create-plan-change-payment-intent`,
    changeData,
  );
export const previewPlanChange = (subscriptionId, changeData) =>
  API.post(`/subscriptions/${subscriptionId}/preview-plan-change`, changeData);
export const updateUserCount = (subscriptionId, userCountData) =>
  API.post(`/subscriptions/${subscriptionId}/update-user-count`, userCountData);
export const switchBillingFrequency = (subscriptionId, billingData) =>
  API.post(`/subscriptions/${subscriptionId}/switch-billing`, billingData);
export const getUpcomingInvoice = (subscriptionId) =>
  API.get(`/subscriptions/${subscriptionId}/upcoming-invoice`);
export const getBillingPortalUrl = () =>
  API.get("/subscriptions/billing-portal");
export const getSubscriptionStats = () => API.get("/subscriptions/stats");
export const getUsageStats = () => API.get("/subscriptions/usage-stats");

// Discount Code APIs
export const validateDiscountCode = (codeData) =>
  API.post("/discount-codes/validate", codeData);
export const applyDiscountCodeUsage = (codeData) =>
  API.post("/discount-codes/apply", codeData);

// Admin Discount Code APIs
export const fetchAllDiscountCodes = (params) => {
  const queryString = params
    ? `?${new URLSearchParams(params).toString()}`
    : "";
  return API.get(`/discount-codes${queryString}`);
};
export const createDiscountCode = (codeData) =>
  API.post("/discount-codes", codeData);
export const updateDiscountCode = (id, codeData) =>
  API.put(`/discount-codes/${id}`, codeData);
export const deleteDiscountCode = (id) => API.delete(`/discount-codes/${id}`);
export const getDiscountCodeStats = (params) => {
  const queryString = params
    ? `?${new URLSearchParams(params).toString()}`
    : "";
  return API.get(`/discount-codes/stats${queryString}`);
};

// QR Code API endpoints
export const createQRCode = (qrCodeData) => API.post("/qrcodes", qrCodeData);
export const getQRCodes = (query, page) => {
  const searchQuery = query?.search || "";
  const type = query?.type || "";
  return API.get(
    `/qrcodes?searchQuery=${searchQuery}&type=${type}&page=${page}`,
  );
};
export const getQRCode = (id) => API.get(`/qrcodes/${id}`);
export const getQRCodeByLink = (link) => API.get(`/qrcodes/public/${link}`);
export const updateQRCode = (id, qrCodeData) =>
  API.patch(`/qrcodes/${id}`, qrCodeData);
export const deleteQRCode = (id) => API.delete(`/qrcodes/${id}`);
export const getQRCodeStatistics = () => API.get("/qrcodes/statistics");
export const generateContentPreview = (data) =>
  API.post("/qrcodes/preview", data);

// Construction Management API endpoints
export const fetchConstructionProjects = (searchQuery, page, pageSize) => {
  const params = new URLSearchParams();
  if (searchQuery?.search) params.append("search", searchQuery.search);
  if (searchQuery?.status) params.append("status", searchQuery.status);
  if (searchQuery?.projectType)
    params.append("projectType", searchQuery.projectType);
  if (page) params.append("page", page);
  if (pageSize) params.append("limit", pageSize);
  return API.get(`/construction/projects?${params.toString()}`);
};

export const fetchConstructionProject = (id) =>
  API.get(`/construction/projects/${id}`);
export const createConstructionProject = (projectData) =>
  API.post("/construction/projects", projectData);
export const updateConstructionProject = (id, projectData) =>
  API.patch(`/construction/projects/${id}`, projectData);
export const deleteConstructionProject = (id) =>
  API.delete(`/construction/projects/${id}`);
export const updateProjectProgress = (id, progressData) =>
  API.patch(`/construction/projects/${id}/progress`, progressData);
export const getProjectDashboard = (id) =>
  API.get(`/construction/projects/${id}/dashboard`);
export const addMilestone = (projectId, milestoneData) =>
  API.post(`/construction/projects/${projectId}/milestones`, milestoneData);
export const updateMilestone = (projectId, milestoneId, milestoneData) =>
  API.patch(
    `/construction/projects/${projectId}/milestones/${milestoneId}`,
    milestoneData,
  );
export const addVariation = (projectId, variationData) =>
  API.post(`/construction/projects/${projectId}/variations`, variationData);
export const approveVariation = (projectId, variationId) =>
  API.patch(
    `/construction/projects/${projectId}/variations/${variationId}/approve`,
  );

// BOQ API endpoints
export const fetchBOQs = (searchQuery, page, pageSize) => {
  const params = new URLSearchParams();
  if (searchQuery?.projectId) params.append("projectId", searchQuery.projectId);
  if (searchQuery?.status) params.append("status", searchQuery.status);
  if (page) params.append("page", page);
  if (pageSize) params.append("limit", pageSize);
  return API.get(`/construction/boq?${params.toString()}`);
};

export const fetchBOQ = (id) => API.get(`/construction/boq/${id}`);
export const createBOQ = (boqData) => API.post("/construction/boq", boqData);
export const updateBOQ = (id, boqData) =>
  API.patch(`/construction/boq/${id}`, boqData);
export const deleteBOQ = (id) => API.delete(`/construction/boq/${id}`);
export const approveBOQ = (id, data) =>
  API.patch(`/construction/boq/${id}/approve`, data);
export const checkBOQ = (id) => API.patch(`/construction/boq/${id}/check`);
export const updateBOQItemProgress = (
  id,
  sectionIndex,
  itemIndex,
  progressData,
) =>
  API.patch(
    `/construction/boq/${id}/sections/${sectionIndex}/items/${itemIndex}/progress`,
    progressData,
  );
export const getBOQSummary = (id) => API.get(`/construction/boq/${id}/summary`);

// Progress Billing API endpoints
export const fetchProgressBillings = (searchQuery, page, pageSize) => {
  const params = new URLSearchParams();
  if (searchQuery?.projectId) params.append("projectId", searchQuery.projectId);
  if (searchQuery?.status) params.append("status", searchQuery.status);
  if (page) params.append("page", page);
  if (pageSize) params.append("limit", pageSize);
  return API.get(`/construction/progress-billing?${params.toString()}`);
};

export const fetchProgressBilling = (id) =>
  API.get(`/construction/progress-billing/${id}`);
export const createProgressBilling = (billingData) =>
  API.post("/construction/progress-billing", billingData);
export const updateProgressBilling = (id, billingData) =>
  API.patch(`/construction/progress-billing/${id}`, billingData);
export const deleteProgressBilling = (id) =>
  API.delete(`/construction/progress-billing/${id}`);
export const submitProgressBilling = (id) =>
  API.patch(`/construction/progress-billing/${id}/submit`);
export const approveProgressBilling = (id) =>
  API.patch(`/construction/progress-billing/${id}/approve`);
export const rejectProgressBilling = (id, data) =>
  API.patch(`/construction/progress-billing/${id}/reject`, data);
export const getProjectBillingSummary = (projectId) =>
  API.get(`/construction/progress-billing/project/${projectId}/summary`);

// Sites API endpoints
export const fetchSites = (searchQuery, page, pageSize) => {
  const params = new URLSearchParams();
  if (searchQuery?.projectId) params.append("projectId", searchQuery.projectId);
  if (searchQuery?.status) params.append("status", searchQuery.status);
  if (page) params.append("page", page);
  if (pageSize) params.append("limit", pageSize);
  return API.get(`/construction/sites?${params.toString()}`);
};

export const fetchSite = (id) => API.get(`/construction/sites/${id}`);
export const createSite = (siteData) =>
  API.post("/construction/sites", siteData);
export const updateSite = (id, siteData) =>
  API.patch(`/construction/sites/${id}`, siteData);
export const deleteSite = (id) => API.delete(`/construction/sites/${id}`);
export const addSiteInventory = (siteId, inventoryData) =>
  API.post(`/construction/sites/${siteId}/inventory`, inventoryData);
export const addSiteWorker = (siteId, workerData) =>
  API.post(`/construction/sites/${siteId}/workers`, workerData);
export const removeSiteWorker = (siteId, workerId) =>
  API.delete(`/construction/sites/${siteId}/workers/${workerId}`);
export const allocateSiteEquipment = (siteId, equipmentData) =>
  API.post(`/construction/sites/${siteId}/equipment`, equipmentData);

// Daily Reports API endpoints
export const fetchDailyReports = (searchQuery, page, pageSize) => {
  const params = new URLSearchParams();
  if (searchQuery?.projectId) params.append("projectId", searchQuery.projectId);
  if (searchQuery?.siteId) params.append("siteId", searchQuery.siteId);
  if (searchQuery?.date) params.append("date", searchQuery.date);
  if (page) params.append("page", page);
  if (pageSize) params.append("limit", pageSize);
  return API.get(`/construction/daily-reports?${params.toString()}`);
};

export const fetchDailyReport = (id) =>
  API.get(`/construction/daily-reports/${id}`);
export const createDailyReport = (reportData) =>
  API.post("/construction/daily-reports", reportData);
export const updateDailyReport = (id, reportData) =>
  API.patch(`/construction/daily-reports/${id}`, reportData);
export const deleteDailyReport = (id) =>
  API.delete(`/construction/daily-reports/${id}`);
export const approveDailyReport = (id) =>
  API.patch(`/construction/daily-reports/${id}/approve`);
export const fetchReportsByDateRange = (startDate, endDate, projectId) => {
  const params = new URLSearchParams();
  params.append("startDate", startDate);
  params.append("endDate", endDate);
  if (projectId) params.append("projectId", projectId);
  return API.get(
    `/construction/daily-reports/range/dates?${params.toString()}`,
  );
};
export const fetchMonthlyReportSummary = (month, year, projectId) => {
  const params = new URLSearchParams();
  params.append("month", month);
  params.append("year", year);
  if (projectId) params.append("projectId", projectId);
  return API.get(
    `/construction/daily-reports/summary/monthly?${params.toString()}`,
  );
};

// Subcontractors API endpoints
export const fetchSubcontractors = (searchQuery, page, pageSize) => {
  const params = new URLSearchParams();
  if (searchQuery?.projectId) params.append("projectId", searchQuery.projectId);
  if (searchQuery?.status) params.append("status", searchQuery.status);
  if (searchQuery?.specialty) params.append("specialty", searchQuery.specialty);
  if (page) params.append("page", page);
  if (pageSize) params.append("limit", pageSize);
  return API.get(`/construction/subcontractors?${params.toString()}`);
};

export const fetchSubcontractor = (id) =>
  API.get(`/construction/subcontractors/${id}`);
export const createSubcontractor = (subcontractorData) =>
  API.post("/construction/subcontractors", subcontractorData);
export const updateSubcontractor = (id, subcontractorData) =>
  API.patch(`/construction/subcontractors/${id}`, subcontractorData);
export const deleteSubcontractor = (id) =>
  API.delete(`/construction/subcontractors/${id}`);
export const recordSubcontractorPayment = (id, paymentData) =>
  API.post(`/construction/subcontractors/${id}/payment`, paymentData);
export const recordSubcontractorBilling = (id, billingData) =>
  API.post(`/construction/subcontractors/${id}/billing`, billingData);
export const updateSubcontractorRating = (id, ratingData) =>
  API.patch(`/construction/subcontractors/${id}/rating`, ratingData);
export const completeSubcontractorWork = (id) =>
  API.patch(`/construction/subcontractors/${id}/complete`);

// Material Requests API endpoints
export const fetchMaterialRequests = (searchQuery, page, pageSize) => {
  const params = new URLSearchParams();
  if (searchQuery?.projectId) params.append("projectId", searchQuery.projectId);
  if (searchQuery?.siteId) params.append("siteId", searchQuery.siteId);
  if (searchQuery?.status) params.append("status", searchQuery.status);
  if (page) params.append("page", page);
  if (pageSize) params.append("limit", pageSize);
  return API.get(`/construction/material-requests?${params.toString()}`);
};

export const fetchPendingMaterialRequests = () =>
  API.get("/construction/material-requests/pending");
export const fetchMaterialRequest = (id) =>
  API.get(`/construction/material-requests/${id}`);
export const createMaterialRequest = (requestData) =>
  API.post("/construction/material-requests", requestData);
export const updateMaterialRequest = (id, requestData) =>
  API.patch(`/construction/material-requests/${id}`, requestData);
export const deleteMaterialRequest = (id) =>
  API.delete(`/construction/material-requests/${id}`);
export const submitMaterialRequest = (id) =>
  API.patch(`/construction/material-requests/${id}/submit`);
export const approveMaterialRequest = (id, data) =>
  API.patch(`/construction/material-requests/${id}/approve`, data);
export const rejectMaterialRequest = (id, data) =>
  API.patch(`/construction/material-requests/${id}/reject`, data);
export const issueMaterials = (id, issueData) =>
  API.patch(`/construction/material-requests/${id}/issue`, issueData);

// Project Budget
export const fetchProjectBudgets = (searchQuery, page, pageSize) =>
  API.get("/construction/budget", {
    params: { ...searchQuery, page, limit: pageSize },
  });
export const fetchProjectBudget = (id) => API.get(`/construction/budget/${id}`);
export const createProjectBudget = (data) =>
  API.post("/construction/budget", data);
export const updateProjectBudget = (id, data) =>
  API.patch(`/construction/budget/${id}`, data);
export const deleteProjectBudget = (id) =>
  API.delete(`/construction/budget/${id}`);

// Project Expenses
export const fetchProjectExpenses = (searchQuery, page, pageSize) =>
  API.get("/construction/expenses", {
    params: { ...searchQuery, page, limit: pageSize },
  });
export const fetchProjectExpense = (id) =>
  API.get(`/construction/expenses/${id}`);
export const createProjectExpense = (data) =>
  API.post("/construction/expenses", data);
export const updateProjectExpense = (id, data) =>
  API.patch(`/construction/expenses/${id}`, data);
export const deleteProjectExpense = (id) =>
  API.delete(`/construction/expenses/${id}`);

// Construction Reports
export const fetchProgressReport = (projectId) =>
  API.get(`/construction/reports/${projectId}/progress`);
export const fetchFinancialReport = (projectId) =>
  API.get(`/construction/reports/${projectId}/financial`);
export const fetchResourceReport = (projectId) =>
  API.get(`/construction/reports/${projectId}/resource`);
export const fetchMaterialReport = (projectId) =>
  API.get(`/construction/reports/${projectId}/material`);
export const fetchScheduleReport = (projectId) =>
  API.get(`/construction/reports/${projectId}/schedule`);
export const fetchOverallAnalytics = (projectId) =>
  API.get(`/construction/reports/${projectId}/analytics`);

export const fetchProfitabilityReport = () =>
  API.get(`/construction/reports/profitability`);

// ============= LAW FIRM MANAGEMENT API ENDPOINTS =============

// Law Case APIs
export const fetchLawCases = (searchQuery, page, pageSize) => {
  const params = new URLSearchParams();
  if (searchQuery) params.append("searchQuery", searchQuery);
  if (page) params.append("page", page);
  if (pageSize) params.append("pageSize", pageSize);
  return API.get(`/law-firm/cases?${params.toString()}`);
};

export const fetchCases = () => API.get("/law-firm/cases");
export const fetchLawCase = (id) => API.get(`/law-firm/cases/${id}`);
export const createLawCase = (caseData) =>
  API.post("/law-firm/cases", caseData);
export const updateLawCase = (id, caseData) =>
  API.patch(`/law-firm/cases/${id}`, caseData);
export const deleteLawCase = (id) => API.delete(`/law-firm/cases/${id}`);
export const closeLawCase = (id, closeData) =>
  API.patch(`/law-firm/cases/${id}/close`, closeData);
export const getCaseDashboard = (id) =>
  API.get(`/law-firm/cases/${id}/dashboard`);
export const performConflictCheck = (conflictData) =>
  API.post("/law-firm/cases/conflict-check", conflictData);
export const addCaseTask = (caseId, taskData) =>
  API.post(`/law-firm/cases/${caseId}/tasks`, taskData);
export const updateCaseTask = (caseId, taskId, taskData) =>
  API.patch(`/law-firm/cases/${caseId}/tasks/${taskId}`, taskData);
export const deleteCaseTask = (caseId, taskId) =>
  API.delete(`/law-firm/cases/${caseId}/tasks/${taskId}`);
export const addCaseNote = (caseId, noteData) =>
  API.post(`/law-firm/cases/${caseId}/notes`, noteData);
export const recordSettlement = (caseId, settlementData) =>
  API.post(`/law-firm/cases/${caseId}/settlement`, settlementData);

// Trial Management APIs
export const getTrialInfo = (caseId) =>
  API.get(`/law-firm/cases/${caseId}/trial`);
export const updateTrialInfo = (caseId, trialData) =>
  API.post(`/law-firm/cases/${caseId}/trial`, trialData);
export const addWitness = (caseId, witnessData) =>
  API.post(`/law-firm/cases/${caseId}/trial/witnesses`, witnessData);
export const updateWitness = (caseId, witnessId, witnessData) =>
  API.patch(
    `/law-firm/cases/${caseId}/trial/witnesses/${witnessId}`,
    witnessData,
  );
export const deleteWitness = (caseId, witnessId) =>
  API.delete(`/law-firm/cases/${caseId}/trial/witnesses/${witnessId}`);
export const addExhibit = (caseId, exhibitData) =>
  API.post(`/law-firm/cases/${caseId}/trial/exhibits`, exhibitData);
export const updateExhibit = (caseId, exhibitId, exhibitData) =>
  API.patch(
    `/law-firm/cases/${caseId}/trial/exhibits/${exhibitId}`,
    exhibitData,
  );
export const deleteExhibit = (caseId, exhibitId) =>
  API.delete(`/law-firm/cases/${caseId}/trial/exhibits/${exhibitId}`);
export const recordVerdict = (caseId, verdictData) =>
  API.post(`/law-firm/cases/${caseId}/trial/verdict`, verdictData);

// Appeal Management APIs
export const getAppealInfo = (caseId) =>
  API.get(`/law-firm/cases/${caseId}/appeal`);
export const fileAppeal = (caseId, appealData) =>
  API.post(`/law-firm/cases/${caseId}/appeal`, appealData);
export const updateAppeal = (caseId, appealData) =>
  API.patch(`/law-firm/cases/${caseId}/appeal`, appealData);
export const recordAppealDecision = (caseId, decisionData) =>
  API.post(`/law-firm/cases/${caseId}/appeal/decision`, decisionData);

// Legal Document Template APIs
export const fetchDocumentTemplates = (filters) =>
  API.get("/law-firm/document-templates", { params: filters });
export const fetchDocumentTemplate = (id) =>
  API.get(`/law-firm/document-templates/${id}`);
export const createDocumentTemplate = async (templateData) => {
  console.log("=== API createDocumentTemplate START ===");
  console.log("API: Sending POST request to /law-firm/document-templates");
  console.log("API: Request data:", templateData);

  try {
    const response = await API.post(
      "/law-firm/document-templates",
      templateData,
    );
    console.log("API: Response received:", response);
    console.log("=== API createDocumentTemplate END (SUCCESS) ===");
    return response;
  } catch (error) {
    console.error("=== API createDocumentTemplate ERROR ===");
    console.error("API Error:", error);
    console.error("API Error response:", error.response);
    console.error("=== API createDocumentTemplate END (ERROR) ===");
    throw error;
  }
};
export const updateDocumentTemplate = (id, templateData) =>
  API.patch(`/law-firm/document-templates/${id}`, templateData);
export const deleteDocumentTemplate = (id) =>
  API.delete(`/law-firm/document-templates/${id}`);
export const generateDocumentFromTemplate = (id, data) =>
  API.post(`/law-firm/document-templates/${id}/generate`, data);
export const duplicateDocumentTemplate = (id) =>
  API.post(`/law-firm/document-templates/${id}/duplicate`);

// Case Document Management APIs
export const fetchCaseDocuments = (caseId, filters) => {
  const params = new URLSearchParams();
  if (filters) {
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && filters[key] !== "")
        params.append(key, filters[key]);
    });
  }
  return API.get(`/law-firm/cases/${caseId}/documents?${params.toString()}`);
};
export const addCaseDocument = (caseId, documentData) =>
  API.post(`/law-firm/cases/${caseId}/documents`, documentData);
export const updateCaseDocument = (caseId, documentId, documentData) =>
  API.patch(`/law-firm/cases/${caseId}/documents/${documentId}`, documentData);
export const deleteCaseDocument = (caseId, documentId) =>
  API.delete(`/law-firm/cases/${caseId}/documents/${documentId}`);
export const searchDocuments = (filters) => {
  const params = new URLSearchParams();
  if (filters) {
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && filters[key] !== "")
        params.append(key, filters[key]);
    });
  }
  return API.get(`/law-firm/documents/search?${params.toString()}`);
};

// Time Entry APIs
export const fetchTimeEntries = (filters) => {
  const params = new URLSearchParams();
  if (filters) {
    Object.keys(filters).forEach((key) => {
      if (filters[key]) params.append(key, filters[key]);
    });
  }
  return API.get(`/law-firm/time-entries?${params.toString()}`);
};

export const fetchCaseTimeEntries = (caseId) =>
  API.get(`/law-firm/cases/${caseId}/time-entries`);
export const fetchTimeEntry = (id) => API.get(`/law-firm/time-entries/${id}`);
export const createTimeEntry = (timeData) =>
  API.post("/law-firm/time-entries", timeData);
export const updateTimeEntry = (id, timeData) =>
  API.patch(`/law-firm/time-entries/${id}`, timeData);
export const deleteTimeEntry = (id) =>
  API.delete(`/law-firm/time-entries/${id}`);
export const approveTimeEntry = (id) =>
  API.patch(`/law-firm/time-entries/${id}/approve`);
export const bulkApproveTimeEntries = (data) =>
  API.post("/law-firm/time-entries/bulk-approve", data);
export const startTimer = (timerData) =>
  API.post("/law-firm/time-entries/timer/start", timerData);
export const stopTimer = (timerId) =>
  API.post("/law-firm/time-entries/timer/stop", { timerId });

// Law Expense APIs
export const fetchLawExpenses = (filters) => {
  const params = new URLSearchParams();
  if (filters) {
    Object.keys(filters).forEach((key) => {
      if (filters[key]) params.append(key, filters[key]);
    });
  }
  return API.get(`/law-firm/expenses?${params.toString()}`);
};

export const fetchCaseExpenses = (caseId) =>
  API.get(`/law-firm/cases/${caseId}/expenses`);
export const fetchLawExpense = (id) => API.get(`/law-firm/expenses/${id}`);
export const createLawExpense = (expenseData) =>
  API.post("/law-firm/expenses", expenseData);
export const updateLawExpense = (id, expenseData) =>
  API.patch(`/law-firm/expenses/${id}`, expenseData);
export const deleteLawExpense = (id) => API.delete(`/law-firm/expenses/${id}`);

// Law Firm Client APIs
export const fetchLawClients = () => API.get("/law-firm/clients");
export const fetchLawClient = (id) => API.get(`/law-firm/clients/${id}`);
export const createLawClient = (clientData) =>
  API.post("/law-firm/clients", clientData);
export const updateLawClient = (id, clientData) =>
  API.patch(`/law-firm/clients/${id}`, clientData);
export const deleteLawClient = (id) => API.delete(`/law-firm/clients/${id}`);

// Trust Account APIs
export const fetchTrustAccounts = () => API.get("/law-firm/trust-accounts");
export const fetchTrustAccount = (id) =>
  API.get(`/law-firm/trust-accounts/${id}`);
export const createTrustAccount = (accountData) =>
  API.post("/law-firm/trust-accounts", accountData);
export const updateTrustAccount = (id, accountData) =>
  API.patch(`/law-firm/trust-accounts/${id}`, accountData);
export const deleteTrustAccount = (id) =>
  API.delete(`/law-firm/trust-accounts/${id}`);
export const addTrustTransaction = (id, transactionData) =>
  API.post(`/law-firm/trust-accounts/${id}/transactions`, transactionData);
export const reconcileTrustAccount = (id, data) =>
  API.post(`/law-firm/trust-accounts/${id}/reconcile`, data);
export const getTrustAccountReport = (id, params) => {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.append("startDate", params.startDate);
  if (params?.endDate) searchParams.append("endDate", params.endDate);
  return API.get(
    `/law-firm/trust-accounts/${id}/report?${searchParams.toString()}`,
  );
};

export const fetchTrustTransactions = (params) => {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.append("startDate", params.startDate);
  if (params?.endDate) searchParams.append("endDate", params.endDate);
  if (params?.accountId) searchParams.append("accountId", params.accountId);
  return API.get(`/law-firm/trust-transactions?${searchParams.toString()}`);
};

// Law Matter APIs
export const fetchLawMatters = (page, pageSize) => {
  const params = new URLSearchParams();
  if (page) params.append("page", page);
  if (pageSize) params.append("pageSize", pageSize);
  return API.get(`/law-firm/matters?${params.toString()}`);
};

export const fetchLawMatter = (id) => API.get(`/law-firm/matters/${id}`);
export const createLawMatter = (matterData) =>
  API.post("/law-firm/matters", matterData);
export const updateLawMatter = (id, matterData) =>
  API.patch(`/law-firm/matters/${id}`, matterData);
export const deleteLawMatter = (id) => API.delete(`/law-firm/matters/${id}`);

// Law Task APIs
export const fetchLawTasks = (filters) => {
  const params = new URLSearchParams();
  if (filters) {
    Object.keys(filters).forEach((key) => {
      if (filters[key]) params.append(key, filters[key]);
    });
  }
  return API.get(`/law-firm/tasks?${params.toString()}`);
};

export const fetchMyTasks = () => API.get("/law-firm/tasks/my-tasks");
export const fetchLawTask = (id) => API.get(`/law-firm/tasks/${id}`);
export const createLawTask = (taskData) =>
  API.post("/law-firm/tasks", taskData);
export const updateLawTask = (id, taskData) =>
  API.patch(`/law-firm/tasks/${id}`, taskData);
export const deleteLawTask = (id) => API.delete(`/law-firm/tasks/${id}`);
export const completeLawTask = (id) =>
  API.patch(`/law-firm/tasks/${id}/complete`);

// Court Calendar APIs
export const fetchCourtEvents = (days = 30) =>
  API.get(`/law-firm/court-dates/upcoming?days=${days}`);
export const fetchCaseCourtDates = (caseId) =>
  API.get(`/law-firm/cases/${caseId}/court-dates`);
export const createCourtEvent = (caseId, courtDateData) =>
  API.post(`/law-firm/cases/${caseId}/court-dates`, courtDateData);
export const updateCourtEvent = (caseId, courtDateId, courtDateData) =>
  API.patch(
    `/law-firm/cases/${caseId}/court-dates/${courtDateId}`,
    courtDateData,
  );
export const deleteCourtEvent = (caseId, courtDateId) =>
  API.delete(`/law-firm/cases/${caseId}/court-dates/${courtDateId}`);
export const calculateCourtDeadlines = (
  caseId,
  courtDateId,
  jurisdiction = "federal",
) =>
  API.post(
    `/law-firm/cases/${caseId}/court-dates/${courtDateId}/calculate-deadlines`,
    { jurisdiction },
  );

// Discovery Management APIs
export const fetchCaseDiscovery = (caseId) =>
  API.get(`/law-firm/cases/${caseId}/discovery`);
export const updateDiscoveryDeadline = (caseId, data) =>
  API.patch(`/law-firm/cases/${caseId}/discovery/deadline`, data);
export const addDiscoveryItem = (caseId, type, data) =>
  API.post(`/law-firm/cases/${caseId}/discovery/${type}`, data);
export const updateDiscoveryItem = (caseId, type, itemId, data) =>
  API.patch(`/law-firm/cases/${caseId}/discovery/${type}/${itemId}`, data);
export const deleteDiscoveryItem = (caseId, type, itemId) =>
  API.delete(`/law-firm/cases/${caseId}/discovery/${type}/${itemId}`);

// Motions & Pleadings APIs
export const fetchCaseMotions = (caseId) =>
  API.get(`/law-firm/cases/${caseId}/motions`);
export const addMotion = (caseId, data) =>
  API.post(`/law-firm/cases/${caseId}/motions`, data);
export const updateMotion = (caseId, motionId, data) =>
  API.patch(`/law-firm/cases/${caseId}/motions/${motionId}`, data);
export const deleteMotion = (caseId, motionId) =>
  API.delete(`/law-firm/cases/${caseId}/motions/${motionId}`);

// Conflict Checker APIs
export const checkConflicts = (searchData) =>
  API.post("/law-firm/conflicts/check", searchData);
export const clearConflict = (conflictId) =>
  API.patch(`/law-firm/conflicts/${conflictId}/clear`);
export const fetchConflictHistory = () =>
  API.get("/law-firm/conflicts/history");

// Base Client Portal APIs (Cross-Module)
export const fetchClientPortalConfig = () => API.get("/client-portal/config");
export const fetchClientPortalDashboard = () =>
  API.get("/client-portal/dashboard");
export const updateClientPortalProfile = (profileData) =>
  API.patch("/client-portal/profile", profileData);
export const changeClientPortalPassword = (passwordData) =>
  API.post("/client-portal/change-password", passwordData);
export const fetchClientPortalNotifications = () =>
  API.get("/client-portal/notifications");
export const markClientPortalNotificationRead = (notificationId) =>
  API.patch(`/client-portal/notifications/${notificationId}/read`);
export const fetchClientPortalActivity = (params) => {
  const searchParams = new URLSearchParams(params);
  return API.get(`/client-portal/activity?${searchParams.toString()}`);
};
export const fetchClientPortalSettings = () =>
  API.get("/client-portal/settings");
export const updateClientPortalSettings = (settings) =>
  API.put("/client-portal/settings", settings);

// Law Firm Client Portal APIs
export const fetchClientPortalData = () =>
  API.get("/law-firm/client-portal/data");
export const fetchClientCaseDetails = (caseId) =>
  API.get(`/law-firm/client-portal/cases/${caseId}`);
export const fetchClientCaseTimeline = (caseId) =>
  API.get(`/law-firm/client-portal/cases/${caseId}/timeline`);
export const downloadClientDocument = (documentId) =>
  API.get(`/law-firm/client-portal/documents/${documentId}/download`, {
    responseType: "blob",
  });
export const sendClientMessage = (messageData) =>
  API.post("/law-firm/client-portal/messages", messageData);
export const makeClientPayment = (paymentData) =>
  API.post("/law-firm/client-portal/payments", paymentData);

// Law Firm Reports APIs
export const getCaseStatistics = () =>
  API.get("/law-firm/reports/case-statistics");
export const getBillingSummary = (params) => {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.keys(params).forEach((key) => {
      if (params[key]) searchParams.append(key, params[key]);
    });
  }
  return API.get(
    `/law-firm/reports/billing-summary?${searchParams.toString()}`,
  );
};
export const getAttorneyPerformance = (params) => {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.keys(params).forEach((key) => {
      if (params[key]) searchParams.append(key, params[key]);
    });
  }
  return API.get(
    `/law-firm/reports/attorney-performance?${searchParams.toString()}`,
  );
};

export const getClientBillingReport = (params) => {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.keys(params).forEach((key) => {
      if (params[key]) searchParams.append(key, params[key]);
    });
  }
  return API.get(`/law-firm/reports/client-billing?${searchParams.toString()}`);
};

export const getCaseProfitabilityReport = (params) => {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.keys(params).forEach((key) => {
      if (params[key]) searchParams.append(key, params[key]);
    });
  }
  return API.get(
    `/law-firm/reports/case-profitability?${searchParams.toString()}`,
  );
};

export const getPracticeAreaReport = (params) => {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.keys(params).forEach((key) => {
      if (params[key]) searchParams.append(key, params[key]);
    });
  }
  return API.get(`/law-firm/reports/practice-areas?${searchParams.toString()}`);
};
export const getTrustSummary = () => API.get("/law-firm/reports/trust-summary");
export const getTimeUtilization = (params) => {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.keys(params).forEach((key) => {
      if (params[key]) searchParams.append(key, params[key]);
    });
  }
  return API.get(
    `/law-firm/reports/time-utilization?${searchParams.toString()}`,
  );
};
export const getCaseProfitability = (caseId) => {
  const params = new URLSearchParams();
  if (caseId) params.append("caseId", caseId);
  return API.get(`/law-firm/reports/case-profitability?${params.toString()}`);
};

// To-Do List API endpoints
export const fetchTodoLists = (page = 1, pageSize = 10) =>
  API.get(`/todo-lists?page=${page}&pageSize=${pageSize}`);

export const fetchTodoListById = (id) => API.get(`/todo-lists/${id}`);

export const addTodoList = (listData) => API.post("/todo-lists", listData);

export const updateTodoList = (id, listData) =>
  API.patch(`/todo-lists/${id}`, listData);

export const deleteTodoList = (id) => API.delete(`/todo-lists/${id}`);

export const shareTodoList = (id, userIds) =>
  API.post(`/todo-lists/${id}/share`, { userIds });

export const unshareFromList = (id, userId) =>
  API.delete(`/todo-lists/${id}/share`, { data: { userId } });

// To-Do Item API endpoints
export const fetchTodoItems = (listId) => API.get(`/todo-items/list/${listId}`);

export const addTodoItem = (listId, itemData) =>
  API.post(`/todo-items/list/${listId}`, itemData);

export const updateTodoItem = (id, itemData) =>
  API.patch(`/todo-items/${id}`, itemData);

export const toggleTodoItem = (id) => API.patch(`/todo-items/${id}/toggle`);

export const deleteTodoItem = (id) => API.delete(`/todo-items/${id}`);

// To-Do Email API endpoints
export const sendTodoListViaEmail = (
  id,
  emails,
  language = "en",
  scheduledAt = null,
) => API.post(`/todo-mail/${id}/send-email`, { emails, language, scheduledAt });

// Credit / Debit Notes API
export const fetchCreditDebitNotes = (params = {}) => {
  const q = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== ""),
    ),
  ).toString();
  return API.get(`/credit-debit-notes${q ? `?${q}` : ""}`);
};
export const fetchCreditDebitNote = (id) =>
  API.get(`/credit-debit-notes/${id}`);
export const fetchCreditDebitNotesByInvoice = (invoiceId) =>
  API.get(`/credit-debit-notes/by-invoice/${invoiceId}`);
export const fetchLastCreditDebitNoteNumber = (noteType) =>
  API.get(`/credit-debit-notes/last-number?noteType=${noteType}`);
export const addCreditDebitNote = (note) =>
  API.post("/credit-debit-notes", note);
export const updateCreditDebitNote = (id, note) =>
  API.patch(`/credit-debit-notes/${id}`, note);
export const deleteCreditDebitNote = (id) =>
  API.delete(`/credit-debit-notes/${id}`);
export const submitCreditDebitNote = (id) =>
  API.patch(`/credit-debit-notes/${id}/submit`);
export const approveCreditDebitNote = (id) =>
  API.patch(`/credit-debit-notes/${id}/approve`);
export const rejectCreditDebitNote = (id, rejectionReason) =>
  API.patch(`/credit-debit-notes/${id}/reject`, { rejectionReason });
export const postCreditDebitNote = (id) =>
  API.patch(`/credit-debit-notes/${id}/post`);

// Inventory Orders API
export const fetchInventoryOrders = (params = {}) => {
  const q = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== ""),
    ),
  ).toString();
  return API.get(`/inventory-orders${q ? `?${q}` : ""}`);
};
export const fetchInventoryOrderByInvoice = (invoiceId) =>
  API.get(`/inventory-orders/by-invoice/${invoiceId}`);
export const fulfilInventoryOrder = (id) =>
  API.patch(`/inventory-orders/${id}/fulfil`, {});
export const cancelInventoryOrder = (id) =>
  API.patch(`/inventory-orders/${id}/cancel`, {});

// ─── Workflow Definitions ────────────────────────────────────────────────────
export const fetchWorkflowDefinitions = () => API.get("/workflows/definitions");
export const fetchWorkflowDefinition = (id) => API.get(`/workflows/definitions/${id}`);
export const createWorkflowDefinition = (data) => API.post("/workflows/definitions", data);
export const updateWorkflowDefinition = (id, data) => API.put(`/workflows/definitions/${id}`, data);
export const deleteWorkflowDefinition = (id) => API.delete(`/workflows/definitions/${id}`);
export const toggleWorkflowDefinition = (id) => API.patch(`/workflows/definitions/${id}/toggle`);

// ─── Workflow Instances ───────────────────────────────────────────────────────
export const launchWorkflowInstance = (data) => API.post("/workflows/instances", data);
export const fetchWorkflowInstance = (id) => API.get(`/workflows/instances/${id}`);
export const fetchWorkflowInstancesForEntity = (type, entityId) =>
  API.get(`/workflows/instances/entity/${type}/${entityId}`);
export const fetchWorkflowInbox = () => API.get("/workflows/instances/inbox");
export const approveWorkflowStep = (id, formData) =>
  API.post(`/workflows/instances/${id}/approve`, formData);
export const rejectWorkflowStep = (id, formData) =>
  API.post(`/workflows/instances/${id}/reject`, formData);
export const addWorkflowComment = (id, formData) =>
  API.post(`/workflows/instances/${id}/comment`, formData);
export const recallWorkflowInstance = (id) => API.post(`/workflows/instances/${id}/recall`);
export const cancelWorkflowInstance = (id) => API.post(`/workflows/instances/${id}/cancel`);
export const fetchWorkflowAuditTrail = (id) => API.get(`/workflows/instances/${id}/audit`);

// ─── Excel Workbooks ──────────────────────────────────────────────────────────
export const fetchExcelWorkbooks = () => API.get("/excel/workbooks");
export const fetchExcelWorkbook = (id) => API.get(`/excel/workbooks/${id}`);
export const createExcelWorkbook = (data) => API.post("/excel/workbooks", data);
export const updateExcelWorkbook = (id, data) => API.patch(`/excel/workbooks/${id}`, data);
export const deleteExcelWorkbook = (id) => API.delete(`/excel/workbooks/${id}`);

// ── AI Integration API ──────────────────────────────────────────────────────
// Providers
export const getAIProviders = () => API.get("/ai/providers");
export const createAIProvider = (data) => API.post("/ai/providers", data);
export const updateAIProvider = (id, data) => API.put(`/ai/providers/${id}`, data);
export const deleteAIProvider = (id) => API.delete(`/ai/providers/${id}`);
export const seedAIProviders = () => API.post("/ai/providers/seed");

// Agents
export const getAIAgents = () => API.get("/ai/agents");
export const createAIAgent = (data) => API.post("/ai/agents", data);
export const updateAIAgent = (id, data) => API.put(`/ai/agents/${id}`, data);
export const deleteAIAgent = (id) => API.delete(`/ai/agents/${id}`);
export const testAIAgent = (id, prompt) => API.post(`/ai/agents/${id}/test`, { prompt });

// User API Keys (per-user)
export const getMyAIKeys = () => API.get("/ai/keys");
export const upsertMyAIKey = (providerId, data) => API.put(`/ai/keys/${providerId}`, data);
export const deleteMyAIKey = (providerId) => API.delete(`/ai/keys/${providerId}`);
export const testMyAIKey = (providerId) => API.post(`/ai/keys/${providerId}/test`);

// Task Types
export const getAITaskTypes = () => API.get("/ai/tasks");
export const assignAIAgent = (slug, agentId) => API.put(`/ai/tasks/${slug}/assign`, { agentId });
export const seedAITaskTypes = () => API.post("/ai/tasks/seed");

// Chat
export const aiChat = (data) => API.post("/ai/chat", data);

// ─── Website Builder ──────────────────────────────────────────────────────────
export const getWebsiteSettings = () => API.get("/website/settings");
export const updateWebsiteSettings = (data) => API.put("/website/settings", data);
export const toggleWebsitePublished = () => API.post("/website/settings/publish");
export const getWebsitePaymentMethods = () => API.get("/website/payment-methods");
export const createWebsitePaymentMethod = (data) => API.post("/website/payment-methods", data);
export const updateWebsitePaymentMethod = (id, data) => API.put(`/website/payment-methods/${id}`, data);
export const deleteWebsitePaymentMethod = (id) => API.delete(`/website/payment-methods/${id}`);

// Export the API instance for direct use
export const api = API;
